/**
 * @file Contains types for Inquiry UI components config and client data.
 * The config represents the actual JSON stored on inquriy template versions.
 * The client data represents what is serialized to clients (web/mobile).
 */
import { RulesLogic } from 'json-logic-js';

import { I18nDynamicVariables } from 'dashboard/models/inquiry-template-version';
import { CountryCode, InquiryLanguage } from 'lib/constants/locales';
import { FileType } from 'lib/file';
import {
  AllStates,
  ButtonStates,
  ConfigValueDirection,
  FormControlStates,
  State,
  Style,
  StyleKey,
  StyleValueAlignment,
  StyleValueAxis,
  StyleValueBoxDimensions,
  StyleValueBoxDimensionsConfig,
  StyleValueColorConfig,
  StyleValueFontFamily,
  StyleValueFontWeight,
  StyleValueFramePositioning,
  StyleValueJustify,
  StyleValueSizeConfig,
  StyleValueXYDimensionsConfig,
} from 'lib/inquiryStyles';
import DeviceHandoffOption from 'lib/interfaces/DeviceHandoffOption';
import type {
  DeviceHandoffAssets,
  DeviceHandoffLocalizations,
  StepUi,
} from 'lib/interfaces/StepsConfig';
import { Iso8601DateString } from 'lib/types';

/**
* Client data
* *

/**
 * Note that this is a subset of:
 * @see UiComponentConfigType
 * Multiple component config types can give the same client data type.
 */
export enum UiComponentType {
  ButtonAction = 'button_action',
  ButtonCancel = 'button_cancel',
  ButtonComplete = 'button_complete',
  ButtonCreatePersona = 'button_create_persona',
  ButtonDeviceHandoff = 'button_device_handoff',
  ButtonLink = 'button_link',
  ButtonSubmit = 'button_submit',
  ButtonVerifyWithPersona = 'button_verify_with_persona',
  CombinedStepButton = 'combined_step/button',
  CombinedStepImagePreview = 'combined_step/image_preview',
  ClickableStack = 'clickable_stack',
  Footer = 'footer',
  GovernmentIdNfcScan = 'government_id_nfc_scan',
  GovernmentIdTypeSelect = 'government_id_type_select',
  HorizontalStack = 'horizontal_stack',
  ImageLocal = 'image_local',
  ImageRemote = 'image_remote',
  InputAddress = 'input_address',
  InputCheckbox = 'input_checkbox',
  InputCheckboxGroup = 'input_checkbox_group',
  InputConfirmationCode = 'input_confirmation_code',
  InputCurrency = 'input_currency',
  InputDate = 'input_date',
  InputESignature = 'input_e_signature',
  InputFileUpload = 'input_file_upload',
  InputInternationalDb = 'input_international_db',
  InputMaskedText = 'input_masked_text',
  InputMultiSelect = 'input_multi_select',
  InputNumber = 'input_number',
  InputPhoneNumber = 'input_phone_number',
  InputRadioGroup = 'input_radio_group',
  InputSelect = 'input_select',
  InputText = 'input_text',
  InputTextArea = 'input_text_area',
  LanguageSelector = 'language_selector',
  PasskeyAuthentication = 'passkey_authentication',
  PasskeyRegistration = 'passkey_registration',
  PersonaBranding = 'persona_branding',
  PrivacyPolicy = 'privacy_policy',
  QrCode = 'qr_code',
  Spacer = 'spacer',
  Stepper = 'stepper',
  TableStatic = 'table_static',
  Text = 'text',
  Title = 'title',
}

export type UiComponent =
  | ButtonActionComponent
  | ButtonCancelComponent
  | ButtonCompleteComponent
  | ButtonCreatePersonaComponent
  | ButtonDeviceHandoffComponent
  | ButtonLinkComponent
  | ButtonSubmitComponent
  | ButtonVerifyWithPersonaComponent
  | LanguageSelectorComponent
  | PasskeyAuthenticationComponent
  | PasskeyRegistrationComponent
  | PersonaBrandingComponent
  | PrivacyPolicyComponent
  | QrCodeComponent
  | SpacerComponent
  | StepperComponent
  | GovernmentIdTypeSelectComponent
  | TableStaticComponent
  | TextComponent
  | TitleComponent
  | GovernmentIdNfcScanComponent
  // Subcategories of components. Components can be members of multiple categories.
  | UiCombinedStepComponent
  | UiFormComponent
  | UiImageComponent
  | UiParentComponent
  | UiTransitionComponent;

/**
 * Components only used in the `pages` attribute of combined steps (aka "nested UI components").
 */
type UiCombinedStepComponent = CombinedStepButtonComponent | CombinedStepImagePreviewComponent;

export type UiImageComponent = ImageLocalComponent | ImageRemoteComponent;

export type SupportedUiComponentType = Exclude<
  UiComponentType,
  | UiComponentType.CombinedStepButton
  | UiComponentType.CombinedStepImagePreview
  | UiComponentType.Stepper
  | UiComponentType.GovernmentIdTypeSelect
>;

/**
 * Form components that support `value` and validations and provide the field
 * values for transitions.
 */
export type UiFormComponent =
  | ButtonSubmitComponent
  | ClickableStackComponent
  | InputAddressComponent
  | InputCheckboxComponent
  | InputCheckboxGroupComponent
  | InputConfirmationCodeComponent
  | InputCurrencyComponent
  | InputDateComponent
  | InputESignatureComponent
  | InputFileUploadComponent
  | InputInternationalDbComponent
  | InputMaskedTextComponent
  | InputMultiSelectComponent
  | InputPhoneNumberComponent
  | InputRadioGroupComponent
  | InputSelectComponent
  | InputTextComponent
  | InputNumberComponent
  | InputTextAreaComponent;

export function isUiFormComponent(
  component: UiComponent | undefined,
): component is UiFormComponent {
  switch (component?.type) {
    case UiComponentType.ButtonSubmit:
    case UiComponentType.ClickableStack:
    case UiComponentType.InputAddress:
    case UiComponentType.InputCheckbox:
    case UiComponentType.InputCheckboxGroup:
    case UiComponentType.InputConfirmationCode:
    case UiComponentType.InputCurrency:
    case UiComponentType.InputDate:
    case UiComponentType.InputESignature:
    case UiComponentType.InputInternationalDb:
    case UiComponentType.InputFileUpload:
    case UiComponentType.InputMaskedText:
    case UiComponentType.InputMultiSelect:
    case UiComponentType.InputNumber:
    case UiComponentType.InputPhoneNumber:
    case UiComponentType.InputRadioGroup:
    case UiComponentType.InputSelect:
    case UiComponentType.InputText:
    case UiComponentType.InputTextArea:
      return true;
    default:
      return false;
  }
}

/**
 * Components that support a `children` config attribute.
 */
export type UiParentComponent =
  | ClickableStackComponent
  | FooterComponent
  | HorizontalStackComponent;

export function isUiParentComponent(
  component: UiComponent | undefined,
): component is UiParentComponent {
  switch (component?.type) {
    case UiComponentType.ClickableStack:
    case UiComponentType.Footer:
    case UiComponentType.HorizontalStack:
      return true;
    default:
      return false;
  }
}

export function isUiFooterComponentConfig(
  component: UiComponentConfig | undefined,
): component is FooterComponentConfig {
  switch (component?.type) {
    case UiComponentConfigType.Footer:
      return true;
    default:
      return false;
  }
}

export const isHorizontalConfigSetting = (direction: ConfigValueDirection) =>
  [ConfigValueDirection.Horizontal, ConfigValueDirection.HorizontalReverse].includes(direction);

export const componentFlexToHorizontalAxis = (component?: UiParentComponent) => {
  if (component == null) return false;
  return (
    ('direction' in component.attributes &&
      isHorizontalConfigSetting(component.attributes.direction)) ||
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    ('axis' in component.styles && component.styles.axis.base.base === 'horizontal')
  );
};

/**
 * Components that support either `to-step` or `workflow-version-id` attributes.
 */
export type UiTransitionComponent =
  | ButtonActionComponent
  | ButtonSubmitComponent
  | ClickableStackComponent
  | ButtonVerifyWithPersonaComponent;

export function isUiTransitionComponent(
  component: UiComponent | undefined,
): component is UiTransitionComponent {
  switch (component?.type) {
    case UiComponentType.ButtonAction:
    case UiComponentType.ButtonSubmit:
    case UiComponentType.ClickableStack:
    case UiComponentType.ButtonVerifyWithPersona:
      return true;
    default:
      return false;
  }
}

/**
 * Components that enable the user to exit the Inquiry flow
 */
type UiTerminalComponent = ButtonCancelComponent | ButtonCompleteComponent;

export function isUiTerminalComponent(
  component: UiComponent | undefined,
): component is UiTerminalComponent {
  switch (component?.type) {
    case UiComponentType.ButtonCancel:
    case UiComponentType.ButtonComplete:
      return true;
    default:
      return false;
  }
}

export type UiButtonComponent =
  | ButtonActionComponent
  | ButtonCompleteComponent
  | ButtonCancelComponent
  | ButtonLinkComponent
  | ButtonSubmitComponent
  | CombinedStepButtonComponent;

export function isUiButtonComponent(
  component: UiComponent | undefined,
): component is UiButtonComponent {
  switch (component?.type) {
    case UiComponentType.ButtonAction:
    case UiComponentType.ButtonSubmit:
    case UiComponentType.ButtonCancel:
    case UiComponentType.ButtonComplete:
    case UiComponentType.ButtonLink:
    case UiComponentType.CombinedStepButton:
      return true;
    default:
      return false;
  }
}

export interface BaseComponent {
  type: UiComponentType;
  name: string;
  attributes: Record<string, unknown>;
  styles?: Record<string, unknown>;
}

export enum ButtonType {
  Primary = 'primary',
  Secondary = 'secondary',
}

type ButtonComponentAttributes = Pick<LogicAttributes, 'disabled' | 'hidden'> & {
  'button-type'?: ButtonType;
  text: string;
};

export type ButtonComponentStyles = {
  // Layout
  height: Style<StyleKey.Height>;
  width: Style<StyleKey.Height>;
  justify: Style<StyleKey.Justify>;
  margin: Style<StyleKey.Margin>;
  padding: Style<StyleKey.Padding>;
  translate: Style<StyleKey.Translate, 'base', ButtonStates>;
  // Text
  'font-family': Style<StyleKey.FontFamily>;
  'font-size': Style<StyleKey.FontSize>;
  'font-weight': Style<StyleKey.FontWeight>;
  'letter-spacing': Style<StyleKey.LetterSpacing>;
  'line-height': Style<StyleKey.LineHeight>;
  'text-color': Style<StyleKey.TextColor, 'base', ButtonStates>;
  // Decoration
  'background-color': Style<StyleKey.BackgroundColor, 'base', ButtonStates>;
  'border-color': Style<StyleKey.BorderColor, 'base', ButtonStates>;
  'border-radius': Style<StyleKey.BorderRadius>;
  'border-width': Style<StyleKey.BorderWidth>;
  'shadow-color': Style<StyleKey.ShadowColor, 'base', ButtonStates>;
  'shadow-offset': Style<StyleKey.ShadowOffset, 'base', ButtonStates>;
  'shadow-radius': Style<StyleKey.ShadowRadius, 'base', ButtonStates>;
  'text-transform': Style<StyleKey.TextTransform>;
};

export type InputDateComponentStyles = {
  'font-family': Style<StyleKey.FontFamily, 'base' | 'value' | 'placeholder' | 'label' | 'error'>;
  'font-size': Style<StyleKey.FontSize, 'value' | 'placeholder' | 'label' | 'error'>;
  'font-weight': Style<StyleKey.FontWeight, 'value' | 'placeholder' | 'label' | 'error'>;
  'letter-spacing': Style<
    StyleKey.LetterSpacing,
    'base' | 'value' | 'placeholder' | 'label' | 'error'
  >;
  'line-height': Style<StyleKey.LineHeight, 'value' | 'placeholder' | 'label' | 'error'>;
  'text-color': Style<
    StyleKey.TextColor,
    'value' | 'placeholder' | 'label' | 'error',
    FormControlStates
  > &
    Style<StyleKey.TextColor, 'input-select'>;
  'background-color': Style<StyleKey.BackgroundColor, 'base', FormControlStates> &
    Style<StyleKey.BackgroundColor, 'input-select'>;
  'border-color': Style<StyleKey.BorderColor, 'base', FormControlStates>;
  'border-radius': Style<StyleKey.BorderRadius>;
  'border-width': Style<StyleKey.BorderWidth>;
  'shadow-color': Style<StyleKey.ShadowColor>;
  'shadow-offset': Style<StyleKey.ShadowOffset>;
  'shadow-radius': Style<StyleKey.ShadowRadius>;
  margin: Style<StyleKey.Margin>;
  justify: Style<StyleKey.Justify>;
  'stroke-color': Style<StyleKey.StrokeColor, 'chevron' | 'header-cancel-button'>;
};

export type InputTextBaseStyles = {
  'font-family': Style<StyleKey.FontFamily, 'base' | 'value' | 'placeholder' | 'label' | 'error'>;
  'font-size': Style<StyleKey.FontSize, 'value' | 'placeholder' | 'label' | 'error'>;
  'font-weight': Style<StyleKey.FontWeight, 'value' | 'placeholder' | 'label' | 'error'>;
  'letter-spacing': Style<
    StyleKey.LetterSpacing,
    'base' | 'value' | 'placeholder' | 'label' | 'error'
  >;
  'line-height': Style<StyleKey.LineHeight, 'value' | 'placeholder' | 'label' | 'error'>;
  margin: Style<StyleKey.Margin>;
  'text-color': Style<
    StyleKey.TextColor,
    'value' | 'placeholder' | 'label' | 'error',
    State.Base | State.Focused | State.Disabled | State.Errored
  >;
  'background-color': Style<
    StyleKey.BackgroundColor,
    'base',
    State.Base | State.Focused | State.Disabled | State.Errored
  >;
  'border-color': Style<
    StyleKey.BorderColor,
    'base',
    State.Base | State.Focused | State.Disabled | State.Errored
  >;
  'border-radius': Style<StyleKey.BorderRadius>;
  'border-width': Style<StyleKey.BorderWidth>;
  'shadow-color': Style<StyleKey.ShadowColor>;
  'shadow-offset': Style<StyleKey.ShadowOffset>;
  'shadow-radius': Style<StyleKey.ShadowRadius>;
};

// This needs to contain all properties in InputComponentTextStyles
export type InputSelectBaseStyles = {
  'background-color': Style<StyleKey.BackgroundColor, 'base', FormControlStates> &
    Style<StyleKey.BackgroundColor, 'option', State.Base | State.Active | State.Focused>;
  margin: Style<StyleKey.Margin, 'base' | 'option'>;
  justify: Style<StyleKey.Justify>;
  'font-family': Style<StyleKey.FontFamily, 'base' | 'error' | 'label' | 'placeholder'>;
  'font-size': Style<StyleKey.FontSize, 'base' | 'value' | 'error' | 'label' | 'placeholder'>;
  'font-weight': Style<StyleKey.FontWeight, 'base' | 'value' | 'error' | 'label' | 'placeholder'>;
  'letter-spacing': Style<StyleKey.LetterSpacing, 'base' | 'error' | 'label' | 'placeholder'>;
  'line-height': Style<StyleKey.LineHeight, 'base' | 'value' | 'label' | 'error' | 'placeholder'>;
  'text-color': Style<StyleKey.TextColor, 'base' | 'error'> &
    Style<StyleKey.TextColor, 'label', State.Base | State.Focused> &
    Style<StyleKey.TextColor, 'value' | 'placeholder', FormControlStates>;
  'border-color': Style<StyleKey.BorderColor, 'base', AllStates>;
  'border-radius': Style<StyleKey.BorderRadius>;
  'border-width': Style<StyleKey.BorderWidth>;
  'stroke-color': Style<StyleKey.StrokeColor, 'chevron' | 'header-cancel-button'>;
  'shadow-color': Style<StyleKey.ShadowColor>;
  'shadow-offset': Style<StyleKey.ShadowOffset>;
  'shadow-radius': Style<StyleKey.ShadowRadius>;
};

export interface ButtonCancelComponent extends BaseComponent {
  type: UiComponentType.ButtonCancel;
  attributes: ButtonComponentAttributes;
  styles?: ButtonComponentStyles;
}

export interface ButtonCompleteComponent extends BaseComponent {
  type: UiComponentType.ButtonComplete;
  attributes: ButtonComponentAttributes & {
    'auto-submit-countdown-text'?: string;
    'auto-submit-interval-seconds'?: number;
  };
  styles?: ButtonComponentStyles;
}

export interface ButtonCreatePersonaComponent extends BaseComponent {
  type: UiComponentType.ButtonCreatePersona;
  attributes: {
    'auto-complete-on-dismiss': boolean;
    'popup-overlay-text': string;
    url: string;
    pages: {
      'cta-card': {
        'ui-step': StepUi;
        'component-name-mapping': {
          'start-button': string;
          'dismiss-button': string;
        };
      };
      'passkey-registration': {
        'ui-step': StepUi;
        'component-name-mapping': {
          'confirm-button': string;
          'cancel-button': string;
        };
      };
      success: {
        'ui-step': StepUi;
        'component-name-mapping': {
          'continue-button': string;
        };
      };
    };
  };
  styles?: {
    margin: Style<StyleKey.Margin>;
    padding: Style<StyleKey.Padding>;
    'background-color': Style<StyleKey.BackgroundColor>;
    'border-color': Style<StyleKey.BorderColor>;
    'border-radius': Style<StyleKey.BorderRadius>;
    'border-width': Style<StyleKey.BorderWidth>;
    'shadow-color': Style<StyleKey.ShadowColor>;
    'shadow-offset': Style<StyleKey.ShadowOffset>;
    'shadow-radius': Style<StyleKey.ShadowRadius>;
  };
}

export interface ButtonVerifyWithPersonaComponent extends BaseComponent {
  type: UiComponentType.ButtonVerifyWithPersona;
  attributes: ButtonComponentAttributes & {
    'popup-overlay-text': string;
    url: string;
    pages: {
      'passkey-authentication': {
        'ui-step': StepUi;
        'component-name-mapping': {
          'confirm-button': string;
          'cancel-button': string;
        };
      };
      success: {
        'ui-step': StepUi;
        'component-name-mapping': {
          'continue-button': string;
        };
      };
      error: {
        'ui-step': StepUi;
        'component-name-mapping': {
          'continue-button': string;
        };
      };
    };
  };
  styles?: ButtonComponentStyles;
}

export interface ButtonDeviceHandoffComponent extends BaseComponent {
  type: UiComponentType.ButtonDeviceHandoff;
  attributes: ButtonComponentAttributes & {
    'device-handoff-options': DeviceHandoffOption[];
    'device-handoff-list-style'?: 'outline' | 'outline_light' | 'button';
    'device-handoff-email-prefill': string;
    'device-handoff-email-disabled': boolean;
    'device-handoff-phone-number-prefill': string;
    'device-handoff-phone-number-disabled': boolean;
    localizations: DeviceHandoffLocalizations;
    assets: DeviceHandoffAssets;
  };
  styles?: ButtonComponentStyles;
}

export interface ButtonActionComponent extends BaseComponent {
  type: UiComponentType.ButtonAction;
  attributes: ButtonComponentAttributes;
  styles?: ButtonComponentStyles;
}

export interface ButtonLinkComponent extends BaseComponent {
  type: UiComponentType.ButtonLink;
  attributes: Pick<LogicAttributes, 'hidden'> & {
    'button-type'?: ButtonType;
    text: string;
    link: string;
  };
  styles?: ButtonComponentStyles;
}

export interface ButtonSubmitComponent extends BaseComponent {
  type: UiComponentType.ButtonSubmit;
  attributes: ButtonComponentAttributes & {
    'auto-submit-countdown-text'?: string;
    'auto-submit-interval-seconds'?: number;
  };
  styles?: ButtonComponentStyles;
}

export interface CombinedStepButtonComponent extends BaseComponent {
  type: UiComponentType.CombinedStepButton;
  attributes: ButtonComponentAttributes;
  styles?: ButtonComponentStyles;
}

export interface CombinedStepImagePreviewComponent extends BaseComponent {
  type: UiComponentType.CombinedStepImagePreview;
  attributes: never;
  styles?: {
    height: Style<StyleKey.Height>;
    width: Style<StyleKey.Width>;
    margin: Style<StyleKey.Margin>;
    justify: Style<StyleKey.Justify>;
  };
}

export interface ParentComponentProps {
  isPreview?: boolean;
  childPreviews?: {
    name: string;
    component: React.ReactNode;
  }[];
}

export interface FooterComponent extends BaseComponent {
  type: UiComponentType.Footer;
  attributes: {
    direction: ConfigValueDirection;
    children: UiComponent[];
  };
  styles?: {
    'background-color': Style<StyleKey.TextColor>;
    padding: Style<StyleKey.Padding>;
    justify: Style<StyleKey.Justify>;
    'border-color': Style<StyleKey.BorderColor>;
    'border-radius': Style<StyleKey.BorderRadius>;
    'border-width': Style<StyleKey.BorderWidth>;
  };
}

export interface GovernmentIdNfcScanComponent extends BaseComponent {
  type: UiComponentType.GovernmentIdNfcScan;
  attributes: {
    disabled?: LogicAttributes['disabled'];
    hidden?: LogicAttributes['hidden'];
    'launch-button-text': string;
    'document-number-label': string;
    'date-of-birth-label': string;
    'expiration-date-label': string;
    'scan-document-prompt-label': string;
    'scan-document-success-label': string;
    authenticating: string;
    reading: string;
    'reading-short-time': string;
    'reading-long-time': string;
    required_text: string;
    'scan-document-error': string;
    'enable-nfc-prompt': string;
    'authentication-error-prompt': string;
    'hide-prefilled-inputs': boolean;
    'required-if-supported': boolean;
    'prefill-document-number'?: string;
    'prefill-date-of-birth'?: Iso8601DateString;
    'prefill-expiration-date'?: Iso8601DateString;
  };
  styles?: {
    'button-primary-style': Style<StyleKey.ButtonPrimaryStyle>;
    'input-text-style': Style<StyleKey.InputTextStyle>;
    'input-date-style': Style<StyleKey.InputDateStyle>;
  };
}

export type StackComponentStyles = {
  margin: Style<StyleKey.Margin>;
  padding: Style<StyleKey.Padding>;
  'frame-positioning': Style<StyleKey.FramePositioning>;
  gap: Style<StyleKey.Gap>;
  alignment: Style<StyleKey.Alignment>;
  justify: Style<StyleKey.Justify>;
  axis: Style<StyleKey.Axis>;
  'background-color': Style<StyleKey.BackgroundColor>;
  'border-color': Style<StyleKey.BorderColor>;
  'border-width': Style<StyleKey.BorderWidth>;
  'border-radius': Style<StyleKey.BorderRadius>;
  'child-sizes': Style<StyleKey.ChildSizes>;
  width: Style<StyleKey.Width>;
};

export interface HorizontalStackComponent extends BaseComponent {
  type: UiComponentType.HorizontalStack;
  attributes: {
    children: UiComponent[];
    hidden: LogicAttributes['hidden'];
  };
  styles?: StackComponentStyles;
}

export interface ClickableStackComponent extends BaseComponent {
  type: UiComponentType.ClickableStack;
  attributes: {
    children: UiComponent[];
    disabled: LogicAttributes['disabled'];
    hidden: LogicAttributes['hidden'];
    'transition-behavior': TransitionBehavior;
  };
  styles?: Omit<StackComponentStyles, 'background-color' | 'border-color'> & {
    'background-color': Style<StyleKey.TextColor, 'base', ButtonStates>;
    'border-color': Style<StyleKey.BorderColor, 'base', ButtonStates>;
  };
}

export type ClickableStackComponentStyles = ClickableStackComponent['styles'];

export enum ImageKey {
  AnimatedCheck = 'animated_check',
  FailedIcon = 'failed_icon',
  StartHero = 'start_hero',

  // Failure Reason Images
  FailureDefaultIcon = 'failure_default_icon',
  FailureDocumentIcon = 'failure_document_icon',
  FailureSelfieIcon = 'failure_selfie_icon',
  IdBackIcon = 'id_back_icon',
  IdFrontIcon = 'id_front_icon',

  DocumentStartHero = 'document_start_hero',

  OptionFlagIcon = 'option_flag_icon',
  OptionGlobeIcon = 'option_globe_icon',
  OptionHomeIcon = 'option_home_icon',
  OptionIdIcon = 'option_id_icon',
  OptionIdDocumentIcon = 'option_id_document_icon',
  OptionUserIcon = 'option_user_icon',
  OptionPhoneIcon = 'option_phone_icon',
  OptionCheckIcon = 'option_check_icon',
  OptionChevronIcon = 'option_chevron_icon',

  CreatePersonaCtaCardIcon = 'create_persona_cta_card_icon',
  CreatePersonaPasskeyRegistrationHero = 'create_persona_passkey_registration_hero',
  CreatePersonaPasskeyRegistrationSpeedIcon = 'create_persona_passkey_registration_speed_icon',
  CreatePersonaPasskeyRegistrationShieldIcon = 'create_persona_passkey_registration_shield_icon',
  CreatePersonaPasskeyRegistrationFingerprintIcon = 'create_persona_passkey_registration_fingerprint_icon',
  CreatePersonaSuccessHero = 'create_persona_success_hero',

  VerifyWithPersonaPasskeyAuthenticationHero = 'verify_with_persona_passkey_authentication_hero',
  VerifyWithPersonaPasskeyAuthenticationPersonaLogo = 'verify_with_persona_passkey_authentication_persona_logo',
  VerifyWithPersonaPasskeyAuthenticationIdIcon = 'verify_with_persona_passkey_authentication_id_icon',
  VerifyWithPersonaPasskeyAuthenticationAddressIcon = 'verify_with_persona_passkey_authentication_address_icon',
  VerifyWithPersonaPasskeyAuthenticationIndividualAttributesIcon = 'verify_with_persona_passkey_authentication_individual_attributes_icon',
  VerifyWithPersonaPasskeyAuthenticationNameIcon = 'verify_with_persona_passkey_authentication_name_icon',
  VerifyWithPersonaPasskeyAuthenticationSelfieIcon = 'verify_with_persona_passkey_authentication_selfie_icon',

  GovernmentIdNfcScanBackClosed = 'government_id_nfc_scan_back_closed',
  GovernmentIdNfcScanFrontClosed = 'government_id_nfc_scan_front_closed',
  GovernmentIdNfcScanPhotoPage = 'government_id_nfc_scan_photo_page',
  GovernmentIdNfcScanIdCard = 'government_id_nfc_scan_id_card',
  GovernmentIdNfcScanBackThenFront = 'government_id_nfc_scan_back_then_front',

  StepperIncompleteIcon = 'stepper_incomplete_icon',
  StepperConnectorIcon = 'stepper_connector_icon',
  StepperCurrentIcon = 'stepper_current_icon',
  StepperCompletedIcon = 'stepper_completed_icon',
  StepperErroredIcon = 'stepper_errored_icon',
}

export interface ImageLocalComponent extends BaseComponent {
  type: UiComponentType.ImageLocal;
  attributes: {
    'image-key': ImageKey;
    hidden?: LogicAttributes['hidden'];
  };
  styles?: {
    margin: Style<StyleKey.Margin>;
    justify: Style<StyleKey.Justify>;
    height: Style<StyleKey.Height> | null;
    width: Style<StyleKey.Width> | null;
    'stroke-color': Style<StyleKey.StrokeColor>;
    'fill-color': Style<StyleKey.FillColor, 'base' | 'background' | 'highlight'>;
  };
}

export type ImageLocalComponentStyles = ImageLocalComponent['styles'];

export interface ImageRemoteComponent extends BaseComponent {
  type: UiComponentType.ImageRemote;
  attributes: {
    url: string;
    'content-type'?: string;
    height?: string;
    width?: string;
    hidden?: LogicAttributes['hidden'];
    alt?: string;
  };
  styles?: {
    margin: Style<StyleKey.Margin>;
    justify: Style<StyleKey.Justify>;
    height: Style<StyleKey.Height>;
    width: Style<StyleKey.Width>;
    'fill-color': Style<
      StyleKey.FillColor,
      | 'fill'
      | 'background'
      | 'highlight'
      | 'original-fill'
      | 'original-background'
      | 'original-highlight'
    >;
    'stroke-color': Style<StyleKey.FillColor, 'stroke' | 'original-stroke'>;
  };
}

export type ImageRemoteComponentStyles = ImageRemoteComponent['styles'];

/**
 * Any valid value a form control can hold.
 * Note that an inquiry Field can potentially hold other things (files, lists...).
 * TODO: maybe use FieldValue[] recursively instead of string[], but limiting
 * to actual use cases for now.
 */
export type FieldValue =
  | string
  | number
  | boolean
  | Iso8601DateString
  | null
  | string[]
  | { [key: string]: string }[]; // input file upload

/**
 * JSONLogic rule with associated error message.
 */
export type UiComponentRule = {
  logic: RulesLogic;
  message: string | null;
};

/**
 * All JSONLogic-powered attributes.
 */
export type LogicAttributes = {
  disabled: RulesLogic | null;
  hidden: RulesLogic | null;
  optional: UiComponentRule;
  validations?: UiComponentRule[];
};

/**
 * Common attributes shared across all form UI components.
 * Does not include label, or any attribute whose type varies based on
 * component type or input layout.
 * Hack: This is a type and not an interface to allow nesting.
 */
type FieldComponentAttributes = LogicAttributes & {
  field: string;
  /**
   * Prefill can be any field value.
   */
  prefill?: FieldValue;
};

export enum AddressAutocompleteMethod {
  None = 'none',
  GoogleMaps = 'google_maps',
}

export enum FormatAddressSubdivision {
  ShortName = 'short_name',
  LongName = 'long_name',
}

export enum PlaceType {
  Geocode = 'geocode',
  Address = 'address',
  Establishment = 'establishment',
}

export interface InputAddressComponent extends BaseComponent {
  type: UiComponentType.InputAddress;
  // Doesn't use FieldComponentattributes b/c this does not represent 1 individual field
  attributes: LogicAttributes &
    Exclude<InputAddressComponentFieldConfig, 'field-key-selected-country-code'> & {
      // prefilled input values
      'prefill-address-street-1': string;
      'prefill-address-street-2': string;
      'prefill-address-city': string;
      'prefill-address-subdivision': string;
      'prefill-address-postal-code': string;
      'prefill-address-business-name': string;
      // validators
      'validations-address-street-1': UiComponentRule[];
      'validations-address-street-2': UiComponentRule[];
      'validations-address-city': UiComponentRule[];
      'validations-address-subdivision': UiComponentRule[];
      'validations-address-postal-code': UiComponentRule[];
      'validations-address-business-name': UiComponentRule[];
      // field values
      'selected-country-code': CountryCode;
      // translations
      'edit-address-manually-prompt': string;
      label?: string;
      'placeholder-autocomplete': string;
      'placeholder-autocomplete-postal-code': string;
      'placeholder-address-street-1': string;
      'placeholder-address-street-2': string;
      'placeholder-address-city': string;
      'placeholder-address-subdivision': string;
      'placeholder-address-subdivision-us': string;
      'placeholder-address-postal-code': string;
      'placeholder-address-postal-code-us': string;
      'placeholder-address-business-name': string;
      // errors
      'error-address-street-1'?: string;
      'error-address-street-2'?: string;
      'error-address-city'?: string;
      'error-address-subdivision'?: string;
      'error-address-postal-code'?: string;
      'error-address-business-name'?: string;
      // other
      'address-autocomplete-method': AddressAutocompleteMethod;
      'format-address-subdivision': FormatAddressSubdivision; // defaults to long_name
      // While Google defines this as an array, only a single item is allowed.
      'place-type'?: PlaceType;
    };
  styles?: InputTextBaseStyles & {
    'text-style': Style<StyleKey.TextStyle, 'expand-button'>;
  };
}

export interface InputInternationalDbComponent extends BaseComponent {
  type: UiComponentType.InputInternationalDb;
  attributes: LogicAttributes & {
    'allowed-id-types': InternationalId[];
    'hide-country-if-prefilled': boolean;
    'hide-type-if-prefilled': boolean;
    'hide-country-if-single-choice': boolean;
    'hide-type-if-single-choice': boolean;
    // Prefills
    'prefill-idb-country'?: string;
    'prefill-idb-type'?: string;
    'prefill-idb-value'?: string;
    // Translations
    label: string;
    'label-idb-country': string;
    'label-idb-type': string;
    'label-idb-value': string;
    'placeholder-idb-country': string;
    'placeholder-idb-type': string;
    'placeholder-idb-value': string;
    // Errors
    'error-idb-country'?: string;
    'error-idb-type'?: string;
    'error-idb-value'?: string;
    // Validations, config logic
    'validations-idb-country': UiComponentRule[];
    'validations-idb-type': UiComponentRule[];
    'validations-idb-value': UiComponentRule[];
  };
  styles?: {
    'input-select-style': Style<StyleKey.InputSelectStyle>;
    'input-text-style': Style<StyleKey.InputTextStyle>;
    'text-style': Style<StyleKey.TextStyle>;
  };
}

export interface InputCheckboxComponent extends BaseComponent {
  type: UiComponentType.InputCheckbox;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: boolean;
    'description-text'?: string;
  };
  styles?: {
    alignment: Style<StyleKey.Alignment>;
    'font-family': Style<StyleKey.FontFamily, 'base' | 'description' | 'error'>;
    'font-size': Style<StyleKey.FontSize, 'base' | 'description' | 'error'>;
    'font-weight': Style<StyleKey.FontWeight, 'base' | 'description' | 'error'>;
    'letter-spacing': Style<StyleKey.LetterSpacing, 'base' | 'description' | 'error'>;
    'line-height': Style<StyleKey.LineHeight, 'base' | 'description' | 'error'>;
    'text-color': Style<StyleKey.TextColor, 'base' | 'description' | 'error'>;
    'text-color-highlight': Style<StyleKey.TextColor>;
    'fill-color': Style<
      StyleKey.FillColor,
      'checkbox-checked' | 'checkbox-unchecked',
      ButtonStates
    >;
    'stroke-color': Style<
      StyleKey.StrokeColor,
      'checkbox-checked' | 'checkbox-unchecked',
      ButtonStates
    > &
      Style<StyleKey.StrokeColor, 'checkmark'>;
  };
}

export interface InputCheckboxGroupComponent extends BaseComponent {
  type: UiComponentType.InputCheckboxGroup;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: string[];
    options: Array<{ value: string; text: string; 'description-text'?: string }>;
  };
  styles?: InputCheckboxComponent['styles'] & {
    'font-family': Style<StyleKey.FontFamily, 'label'>;
    'font-size': Style<StyleKey.FontSize, 'label'>;
    'font-weight': Style<StyleKey.FontWeight, 'label'>;
    'letter-spacing': Style<StyleKey.LetterSpacing, 'label'>;
    'line-height': Style<StyleKey.LineHeight, 'label'>;
    'text-color': Style<StyleKey.TextColor, 'label', State.Base | State.Focused>;
  };
}

export interface InputConfirmationCodeComponent extends BaseComponent {
  type: UiComponentType.InputConfirmationCode;
  attributes: LogicAttributes;
  styles?: InputTextBaseStyles;
}

export interface InputDateComponent extends BaseComponent {
  type: UiComponentType.InputDate;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: Iso8601DateString;
    'placeholder-month': string;
    'placeholder-day': string;
    'placeholder-year': string;
    'label-month': string;
    'label-day': string;
    'label-year': string;
    'text-months': string[];
  };
  styles?: InputDateComponentStyles;
}

export interface InputESignatureComponent extends BaseComponent {
  type: UiComponentType.InputESignature;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: string;
    placeholder?: string;
    'dialog-title': string;
    'dialog-text': string;
    'save-button-text': string;
    'clear-button-text': string;
  };
  styles?: InputTextBaseStyles & {
    'fill-color': Style<StyleKey.FillColor>;
    'background-color': Style<
      StyleKey.BackgroundColor,
      'base',
      State.Base | State.Focused | State.Disabled | State.Errored
    > &
      Style<StyleKey.BackgroundColor, 'signature-preview'>;
    'button-primary-style': Style<StyleKey.ButtonPrimaryStyle, 'save-signature-button'>;
    'button-secondary-style': Style<StyleKey.ButtonSecondaryStyle, 'clear-signature-button'>;
    'title-style': Style<StyleKey.TitleStyle, 'dialog-title'>;
    'text-style': Style<StyleKey.TextStyle, 'dialog-text'>;
  };
}

export interface InputMaskedTextComponent extends BaseComponent {
  type: UiComponentType.InputMaskedText;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: string;
    placeholder?: string;
    secure?: boolean;
    mask?: string;
    error?: string;
  };
  styles?: InputTextBaseStyles;
}

export interface InputMultiSelectComponent extends BaseComponent {
  type: UiComponentType.InputMultiSelect;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: string[];
    placeholder?: string;
    options: Array<{ value: string; text: string }>;
  };
  styles?: InputSelectBaseStyles;
}

export interface InputPhoneNumberComponent extends BaseComponent {
  type: UiComponentType.InputPhoneNumber;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: string;
    placeholder?: string;
    'country-code'?: string;
    'disable-country-code-selection'?: boolean;
    'phone-number-format'?: PhoneNumberFormat;
    error?: string;
  };
  styles?: InputTextBaseStyles;
}

export interface InputRadioGroupComponent extends BaseComponent {
  type: UiComponentType.InputRadioGroup;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: string;
    options: Array<{ value: string; text: string; 'description-text'?: string }>;
  };
  styles?: {
    'font-family': Style<StyleKey.FontFamily, 'base' | 'description' | 'error' | 'label'>;
    'font-size': Style<StyleKey.FontSize, 'base' | 'description' | 'error' | 'label'>;
    'font-weight': Style<StyleKey.FontWeight, 'base' | 'description' | 'error' | 'label'>;
    'letter-spacing': Style<StyleKey.LetterSpacing, 'base' | 'description' | 'error' | 'label'>;
    'line-height': Style<StyleKey.LineHeight, 'base' | 'description' | 'error' | 'label'>;
    'text-color': Style<StyleKey.TextColor, 'base' | 'description' | 'error' | 'label'> &
      Style<StyleKey.TextColor, 'label', State.Focused>;
    'text-color-highlight': Style<StyleKey.TextColor>;
  };
}

export interface InputSelectComponent extends BaseComponent {
  type: UiComponentType.InputSelect;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: string;
    placeholder?: string;
    options: Array<{ value: string; text: string }>;
  };
  styles?: InputSelectBaseStyles;
}

export enum InputType {
  Text = 'text',
  Email = 'email',
  NumberPad = 'number_pad',
}

export enum AutofillHint {
  NameFirst = 'name_first',
  NameMiddle = 'name_middle',
  NameLast = 'name_last',
  Email = 'email',
  AddressLine1 = 'address_line_1',
  AddressLine2 = 'address_line_2',
  City = 'city',
  Country = 'country',
  PostalCode = 'postal_code',
}

export interface InputTextComponent extends BaseComponent {
  type: UiComponentType.InputText;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: string;
    placeholder?: string;
    'input-type'?: InputType;
    'autofill-hint'?: AutofillHint;
    error?: string;
  };
  styles?: InputTextBaseStyles;
}

export interface InputNumberComponent extends BaseComponent {
  type: UiComponentType.InputNumber;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: number;
    placeholder?: string;
    precision?: number;
  };
  styles?: InputTextBaseStyles;
}

export interface InputCurrencyComponent extends BaseComponent {
  type: UiComponentType.InputCurrency;
  attributes: FieldComponentAttributes & {
    label: string;
    prefill: number | null;
    placeholder: string;
    'currency-code': string;
  };
  styles?: InputTextBaseStyles;
}

export interface InputTextAreaComponent extends BaseComponent {
  type: UiComponentType.InputTextArea;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill?: string;
    placeholder?: string;
    'allow-resize': boolean;
    rows: number;
  };
  styles?: InputTextBaseStyles;
}

export interface InternationalId {
  name: string;
  mask: string;
  description: string;
  placeholder: string;
  secure: boolean;
  'country-code': string;
  'country-name': string;
  'id-type': string;
}

export interface InputFileUploadComponent extends BaseComponent {
  type: UiComponentType.InputFileUpload;
  attributes: FieldComponentAttributes & {
    label?: string;
    prefill:
      | {
          filename: string;
          url: string;
        }[]
      | string[];
    'file-upload-limit': number;
    'allowed-file-types': AllowedFileUploadTypes[];
  };
  styles?: InputTextBaseStyles; // TODO: [File Upload] update with back end style definition
}

/**
 * @see Inquiry::UIComponent::InputFileUpload::ALLOWED_FILE_TYPES
 */
export type AllowedFileUploadTypes =
  | FileType.ImageJpg
  | FileType.ImageJpeg
  | FileType.ImagePng
  | FileType.ImageHeic
  | FileType.ImageHeif
  | FileType.ImageTif
  | FileType.ImageTiff
  | FileType.ApplicationPdf;

export interface LanguageSelectorComponent extends BaseComponent {
  type: UiComponentType.LanguageSelector;
  attributes: {
    'enabled-locales': InquiryLanguage[];
  };
}

export interface PasskeyAuthenticationComponent extends BaseComponent {
  type: UiComponentType.PasskeyAuthentication;
  attributes: {
    label: string;
  };
}

export interface PasskeyRegistrationComponent extends BaseComponent {
  type: UiComponentType.PasskeyRegistration;
  attributes: {
    label: string;
    text: string;
  };
}

export interface PersonaBrandingComponent extends BaseComponent {
  type: UiComponentType.PersonaBranding;
  attributes: Record<string, never>;
}

export interface PrivacyPolicyComponent extends BaseComponent {
  type: UiComponentType.PrivacyPolicy;
  attributes: {
    text: string;
    hidden?: LogicAttributes['hidden'];
  };
  styles?: {
    margin: Style<StyleKey.Margin>;
    justify: Style<StyleKey.Justify>;
    'font-family': Style<StyleKey.FontFamily>;
    'font-size': Style<StyleKey.FontSize>;
    'font-weight': Style<StyleKey.FontWeight>;
    'letter-spacing': Style<StyleKey.LetterSpacing>;
    'line-height': Style<StyleKey.LineHeight>;
    'text-color': Style<StyleKey.TextColor>;
    'text-color-highlight': Style<StyleKey.TextColor>;
  };
}

export interface QrCodeComponent extends BaseComponent {
  type: UiComponentType.QrCode;
  attributes: {
    value: string;
    hidden?: LogicAttributes['hidden'];
  };
  styles?: {
    width: Style<StyleKey.Width>;
    margin: Style<StyleKey.Margin>;
    justify: Style<StyleKey.Justify>;
    'fill-color': Style<StyleKey.FillColor>;
    'stroke-color': Style<StyleKey.StrokeColor>;
  };
}

export interface SpacerComponent extends BaseComponent {
  type: UiComponentType.Spacer;
  attributes: {
    height: string;
    hidden: LogicAttributes['hidden'];
  };
  styles?: {
    height: Style<StyleKey.Height>;
    width: Style<StyleKey.Width>;
  };
}

// FIXME: This is just a dummy type for now to get the tsc checks to work.
//  Currently, stepper configs are represented in client data by horizontal stack.
//  We have not nailed down whether this is appropriate for stepper or if stepper
//  should have its own client type
export interface StepperComponent extends BaseComponent {
  type: UiComponentType.Stepper;
}

// FIXME: This is just a dummy type for now to get the tsc checks to work.
//  Currently, gov id type select configs are represented in client data by horizontal stack.
//  We have not nailed down whether this is appropriate or if it should have its own client type
export interface GovernmentIdTypeSelectComponent extends BaseComponent {
  type: UiComponentType.GovernmentIdTypeSelect;
}

export interface TableStaticComponent extends BaseComponent {
  type: UiComponentType.TableStatic;
  attributes: {
    header: string[];
    rows: string[][];
  };
}

export interface TextComponent extends BaseComponent {
  type: UiComponentType.Text;
  attributes: {
    text: string;
    hidden?: LogicAttributes['hidden'];
  };
  styles?: {
    margin: Style<StyleKey.Margin>;
    justify: Style<StyleKey.Justify>;
    'font-family': Style<StyleKey.FontFamily>;
    'font-size': Style<StyleKey.FontSize>;
    'font-weight': Style<StyleKey.FontWeight>;
    'letter-spacing': Style<StyleKey.LetterSpacing>;
    'line-height': Style<StyleKey.LineHeight>;
    'text-color': Style<StyleKey.TextColor>;
    'text-color-highlight': Style<StyleKey.TextColor>;
  };
}

export type TextComponentStyles = TextComponent['styles'];

export interface TitleComponent extends BaseComponent {
  type: UiComponentType.Title;
  attributes: {
    text: string;
    hidden?: LogicAttributes['hidden'];
  };
  styles?: {
    margin: Style<StyleKey.Margin>;
    justify: Style<StyleKey.Justify>;
    'font-family': Style<StyleKey.FontFamily>;
    'font-size': Style<StyleKey.FontSize>;
    'font-weight': Style<StyleKey.FontWeight>;
    'letter-spacing': Style<StyleKey.LetterSpacing>;
    'line-height': Style<StyleKey.LineHeight>;
    'text-color': Style<StyleKey.TextColor>;
    'text-color-highlight': Style<StyleKey.TextColor>;
  };
}

export type TitleComponentStyles = TitleComponent['styles'];

// Components with editable display translations
export type UiTextComponent =
  | ButtonActionComponent
  | ButtonCancelComponent
  | ButtonCompleteComponent
  | ButtonDeviceHandoffComponent
  | ButtonLinkComponent
  | ButtonSubmitComponent
  | CombinedStepButtonComponent
  | GovernmentIdNfcScanComponent
  | InputAddressComponent
  | InputCheckboxComponent
  | InputCheckboxGroupComponent
  | InputDateComponent
  | InputESignatureComponent
  | InputInternationalDbComponent
  | InputMaskedTextComponent
  | InputMultiSelectComponent
  | InputNumberComponent
  | InputPhoneNumberComponent
  | InputRadioGroupComponent
  | InputSelectComponent
  | InputTextComponent
  | InputTextAreaComponent
  | TextComponent
  | TitleComponent;

export function isTextComponent(component: UiComponent | undefined): component is UiTextComponent {
  switch (component?.type) {
    case UiComponentType.ButtonAction:
    case UiComponentType.ButtonCancel:
    case UiComponentType.ButtonComplete:
    case UiComponentType.ButtonDeviceHandoff:
    case UiComponentType.ButtonLink:
    case UiComponentType.ButtonSubmit:
    case UiComponentType.ButtonVerifyWithPersona:
    case UiComponentType.CombinedStepButton:
    case UiComponentType.GovernmentIdNfcScan:
    case UiComponentType.InputAddress:
    case UiComponentType.InputCheckbox:
    case UiComponentType.InputCheckboxGroup:
    case UiComponentType.InputDate:
    case UiComponentType.InputESignature:
    case UiComponentType.InputInternationalDb:
    case UiComponentType.InputMaskedText:
    case UiComponentType.InputMultiSelect:
    case UiComponentType.InputNumber:
    case UiComponentType.InputPhoneNumber:
    case UiComponentType.InputRadioGroup:
    case UiComponentType.InputSelect:
    case UiComponentType.InputText:
    case UiComponentType.InputTextArea:
    case UiComponentType.InputFileUpload:
    case UiComponentType.Text:
    case UiComponentType.Title:
      return true;
    default:
      return false;
  }
}

export function isImageComponent(
  component: UiComponent | undefined,
): component is UiImageComponent {
  switch (component?.type) {
    case UiComponentType.ImageLocal:
    case UiComponentType.ImageRemote:
      return true;
    default:
      return false;
  }
}

export type UiPrefillTextComponent =
  | InputCurrencyComponent
  | InputMaskedTextComponent
  | InputNumberComponent
  | InputPhoneNumberComponent
  | InputSelectComponent
  | InputTextComponent
  | InputTextAreaComponent;

export function isPrefillTextComponent(
  component: UiComponent | undefined,
): component is UiPrefillTextComponent {
  switch (component?.type) {
    case UiComponentType.InputCurrency:
    case UiComponentType.InputMaskedText:
    case UiComponentType.InputNumber:
    case UiComponentType.InputPhoneNumber:
    case UiComponentType.InputSelect:
    case UiComponentType.InputText:
    case UiComponentType.InputTextArea:
      return true;
    default:
      return false;
  }
}

/**
 * Server data
 */

/**
 * These differs from the UiComponent type: it has key `config` instead of
 * `attributes`, and some config values are different, e.g. locale-aware ones.
 *
 * Note that this is a superset of:
 * @see UiComponentType
 * Multiple component types can give the same client data type.
 */
export enum UiComponentConfigType {
  ButtonAction = 'button_action',
  ButtonCancel = 'button_cancel',
  ButtonComplete = 'button_complete',
  ButtonDeviceHandoff = 'button_device_handoff',
  ButtonLink = 'button_link',
  ButtonSubmit = 'button_submit',
  ButtonVerifyWithPersona = 'button_verify_with_persona',
  CombinedStepButton = 'combined_step/button',
  CombinedStepImagePreview = 'combined_step/image_preview',
  ClickableStack = 'clickable_stack',
  FailureReasonText = 'failure_reason_text',
  FailureReasonImage = 'failure_reason_image',
  Footer = 'footer',
  GovernmentIdNfcScan = 'government_id_nfc_scan',
  GovernmentIdTypeSelect = 'government_id_type_select',
  HorizontalStack = 'horizontal_stack',
  ImageHosted = 'image_hosted',
  ImageLocal = 'image_local',
  InputAddress = 'input_address',
  InputCheckbox = 'input_checkbox',
  InputCheckboxDatetime = 'input_checkbox_datetime',
  InputCheckboxGroup = 'input_checkbox_group',
  InputConfirmationCode = 'input_confirmation_code',
  InputCountrySelect = 'input_country_select',
  InputCurrency = 'input_currency',
  InputDate = 'input_date',
  InputESignature = 'input_e_signature',
  InputFileUpload = 'input_file_upload',
  InputInternationalDb = 'input_international_db',
  InputMaskedText = 'input_masked_text',
  InputMultiSelect = 'input_multi_select',
  InputNumber = 'input_number',
  InputPhoneNumber = 'input_phone_number',
  InputRadioGroup = 'input_radio_group',
  InputSelect = 'input_select',
  InputSubdivisionSelect = 'input_subdivision_select',
  InputText = 'input_text',
  InputTextArea = 'input_text_area',
  InputUrl = 'input_url',
  LanguageSelector = 'language_selector',
  PasskeyAuthentication = 'passkey_authentication',
  PasskeyRegistration = 'passkey_registration',
  PersonaBranding = 'persona_branding',
  PrivacyPolicy = 'privacy_policy',
  QrCode = 'qr_code',
  Spacer = 'spacer',
  Stepper = 'stepper',
  TableStatic = 'table_static',
  Text = 'text',
  Title = 'title',
}

/**
 * Components not supported within the web client
 * They should be removed once they are
 */
export const SupportedUiComponentConfigTypes = Object.values(UiComponentConfigType).filter(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (type) => ![].includes(type),
);

export interface Translation {
  locale: InquiryLanguage;
  value: string | string[];
}

interface MessageConfig {
  key: string;
  'text-translations'?: Translation[];
}

export type UiComponentConfig =
  | ButtonCancelComponentConfig
  | ButtonCompleteComponentConfig
  | ButtonDeviceHandoffComponentConfig
  | ButtonLinkComponentConfig
  | ButtonVerifyWithPersonaComponentConfig
  | GovernmentIdNfcScanComponentConfig
  | LanguageSelectorComponentConfig
  | PasskeyAuthenticationComponentConfig
  | PasskeyRegistrationComponentConfig
  | PersonaBrandingComponentConfig
  | PrivacyPolicyComponentConfig
  | QrCodeComponentConfig
  | SpacerComponentConfig
  | TableStaticComponentConfig
  | TextComponentConfig
  | TitleComponentConfig
  | InputUrlComponentConfig
  | InputFileUploadComponentConfig
  // Subcategories of components. Components can be members of multiple categories.
  | UiCombinedStepComponentConfig
  | UiFormComponentConfig
  | UiImageComponentConfig
  | UiParentComponentConfig
  | UiTransitionComponentConfig
  // Components that are not supported within the web client
  | GovernmentIdTypeSelectComponentConfig
  | StepperComponentConfig;

/**
 * Components only used in the `pages` attribute of combined steps (aka "nested UI components").
 */
type UiCombinedStepComponentConfig =
  | CombinedStepButtonComponentConfig
  | CombinedStepImagePreviewComponentConfig;

export type UiImageComponentConfig =
  | ImageLocalComponentConfig
  | ImageHostedComponentConfig
  | StepperComponentConfig;

/**
 * Form components that reference ITV fields.
 * Currently UiFormFieldComponentConfig (single field) +
 * InputAddressComponentConfig and InputInternationalDbComponentConfig,
 * which reference multiple
 */
export type UiFormComponentConfig = UiFormMultiFieldComponentConfig | UiFormFieldComponentConfig;

export type UiFormMultiFieldComponentConfig =
  | InputAddressComponentConfig
  | InputInternationalDbComponentConfig;

export type UiFormFieldComponentConfig =
  | InputCheckboxComponentConfig
  | InputCheckboxDatetimeComponentConfig
  | InputCountrySelectComponentConfig
  | InputCurrencyComponentConfig
  | InputDateComponentConfig
  | InputESignatureComponentConfig
  | InputMaskedTextComponentConfig
  | InputNumberComponentConfig
  | InputPhoneNumberComponentConfig
  | InputSubdivisionSelectComponentConfig
  | InputTextComponentConfig
  | InputTextAreaComponentConfig
  | InputFileUploadComponentConfig
  | InputUrlComponentConfig
  | GovernmentIdTypeSelectComponentConfig
  | UiFormFieldOptionComponentConfig;

export type UiFormFieldOptionComponentConfig =
  | InputCheckboxGroupComponentConfig
  | InputSelectComponentConfig
  | InputMultiSelectComponentConfig
  | InputRadioGroupComponentConfig;

/**
 * Components that can set field values on the inquiry on-transition
 * via Inquiry#augment_from_component_field but do not a require the
 * field attribute to be set like the UiFormComponentConfig components.
 */
export type UiFormOptionalFieldComponentConfig =
  | ButtonSubmitComponentConfig
  | ClickableStackComponentConfig
  | GovernmentIdNfcScanComponentConfig;

export function isUiFormFieldComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiFormFieldComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.InputCheckbox:
    case UiComponentConfigType.InputCheckboxDatetime:
    case UiComponentConfigType.InputCheckboxGroup:
    case UiComponentConfigType.InputCountrySelect:
    case UiComponentConfigType.InputCurrency:
    case UiComponentConfigType.InputDate:
    case UiComponentConfigType.InputESignature:
    case UiComponentConfigType.InputMaskedText:
    case UiComponentConfigType.InputMultiSelect:
    case UiComponentConfigType.InputNumber:
    case UiComponentConfigType.InputPhoneNumber:
    case UiComponentConfigType.InputRadioGroup:
    case UiComponentConfigType.InputSelect:
    case UiComponentConfigType.InputSubdivisionSelect:
    case UiComponentConfigType.InputText:
    case UiComponentConfigType.InputTextArea:
    case UiComponentConfigType.InputFileUpload:
    case UiComponentConfigType.InputUrl:
    case UiComponentConfigType.GovernmentIdTypeSelect:
      return true;
    default:
      return false;
  }
}

export function isUiFormFieldOptionComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiFormFieldOptionComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.InputSelect:
    case UiComponentConfigType.InputMultiSelect:
    case UiComponentConfigType.InputRadioGroup:
    case UiComponentConfigType.InputCheckboxGroup:
      return true;
    default:
      return false;
  }
}

export function isUiFormFieldDynamicOptionComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiFormFieldOptionComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.InputSelect:
      // TODO(ernest): uncomment these as they are supported for dynamic options
      // case UiComponentConfigType.INPUT_MULTI_SELECT:
      return true;
    default:
      return false;
  }
}

export function isUiFormMultiFieldComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiFormMultiFieldComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.InputAddress:
    case UiComponentConfigType.InputInternationalDb:
      return true;
    default:
      return false;
  }
}

export type MultiFieldKey<C> = C extends { config: infer Config }
  ? Extract<keyof Config, `field-key-${string}`>
  : never;
export type FieldKey<C extends UiFormComponentConfig> = C extends UiFormMultiFieldComponentConfig
  ? MultiFieldKey<C>
  : C extends UiFormFieldComponentConfig
    ? 'field'
    : never;

export type UiFormFieldOptionComponent =
  | InputSelectComponent
  | InputMultiSelectComponent
  | InputRadioGroupComponent
  | InputCheckboxGroupComponent;

export function isUiFormFieldOptionComponent(
  component: UiComponent | undefined,
): component is UiFormFieldOptionComponent {
  switch (component?.type) {
    case UiComponentType.InputSelect:
    case UiComponentType.InputMultiSelect:
    case UiComponentType.InputCheckboxGroup:
    case UiComponentType.InputRadioGroup:
      return true;
    default:
      return false;
  }
}

export function isUiFormComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiFormComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.InputAddress:
    case UiComponentConfigType.InputInternationalDb:
      return true;
    default:
      return isUiFormFieldComponentConfig(componentConfig);
  }
}

// Component configs with editable translations
export type UiTextComponentConfig =
  | ButtonActionComponentConfig
  | ButtonCancelComponentConfig
  | ButtonCompleteComponentConfig
  | ButtonDeviceHandoffComponentConfig
  | ButtonLinkComponentConfig
  | ButtonSubmitComponentConfig
  | ButtonVerifyWithPersonaComponentConfig
  | CombinedStepButtonComponentConfig
  | GovernmentIdNfcScanComponentConfig
  | InputAddressComponentConfig
  | InputCheckboxComponentConfig
  | InputCheckboxDatetimeComponentConfig
  | InputCheckboxGroupComponentConfig
  | InputCountrySelectComponentConfig
  | InputCurrencyComponentConfig
  | InputDateComponentConfig
  | InputESignatureComponentConfig
  | InputInternationalDbComponentConfig
  | InputMaskedTextComponentConfig
  | InputMultiSelectComponentConfig
  | InputNumberComponentConfig
  | InputPhoneNumberComponentConfig
  | InputRadioGroupComponentConfig
  | InputSelectComponentConfig
  | InputSubdivisionSelectComponentConfig
  | InputTextComponentConfig
  | InputTextAreaComponentConfig
  | InputUrlComponentConfig
  | TextComponentConfig
  | TitleComponentConfig
  | StepperComponentConfig;

export function isTextComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiTextComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.ButtonAction:
    case UiComponentConfigType.ButtonCancel:
    case UiComponentConfigType.ButtonComplete:
    case UiComponentConfigType.ButtonDeviceHandoff:
    case UiComponentConfigType.ButtonLink:
    case UiComponentConfigType.ButtonSubmit:
    case UiComponentConfigType.ButtonVerifyWithPersona:
    case UiComponentConfigType.CombinedStepButton:
    case UiComponentConfigType.GovernmentIdNfcScan:
    case UiComponentConfigType.InputAddress:
    case UiComponentConfigType.InputCheckbox:
    case UiComponentConfigType.InputCheckboxDatetime:
    case UiComponentConfigType.InputCheckboxGroup:
    case UiComponentConfigType.InputCountrySelect:
    case UiComponentConfigType.InputCurrency:
    case UiComponentConfigType.InputDate:
    case UiComponentConfigType.InputESignature:
    case UiComponentConfigType.InputInternationalDb:
    case UiComponentConfigType.InputMaskedText:
    case UiComponentConfigType.InputMultiSelect:
    case UiComponentConfigType.InputNumber:
    case UiComponentConfigType.InputPhoneNumber:
    case UiComponentConfigType.InputRadioGroup:
    case UiComponentConfigType.InputSelect:
    case UiComponentConfigType.InputFileUpload:
    case UiComponentConfigType.InputSubdivisionSelect:
    case UiComponentConfigType.InputText:
    case UiComponentConfigType.InputTextArea:
    case UiComponentConfigType.InputUrl:
    case UiComponentConfigType.Text:
    case UiComponentConfigType.Title:
      return true;
    default:
      return false;
  }
}

export function isImageComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiImageComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.ImageHosted:
    case UiComponentConfigType.ImageLocal:
      return true;
    default:
      return false;
  }
}

export function isImageHostedComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiImageComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.ImageHosted:
      return true;
    default:
      return false;
  }
}

/**
 * Components that support a `children` config attribute.
 */
export type UiParentComponentConfig =
  | ClickableStackComponentConfig
  | FooterComponentConfig
  | HorizontalStackComponentConfig;

export function isUiParentComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiParentComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.ClickableStack:
    case UiComponentConfigType.Footer:
    case UiComponentConfigType.HorizontalStack:
      return true;
    default:
      return false;
  }
}

/**
 * Components that support either `to-step` or `workflow-version-id` attributes.
 */
export type UiTransitionComponentConfig =
  | ButtonActionComponentConfig
  | ButtonSubmitComponentConfig
  | ButtonVerifyWithPersonaComponentConfig
  | ClickableStackComponentConfig
  | GovernmentIdTypeSelectComponentConfig;

export function isUiTransitionComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiTransitionComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.ButtonAction:
    case UiComponentConfigType.ButtonSubmit:
    case UiComponentConfigType.ButtonVerifyWithPersona:
    case UiComponentConfigType.ClickableStack:
    case UiComponentConfigType.GovernmentIdTypeSelect:
      return true;
    default:
      return false;
  }
}

export function isButtonSubmitComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is ButtonSubmitComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.ButtonSubmit:
      return true;
    default:
      return false;
  }
}

type TransitionBehavior = 'submit' | 'action';

/**
 * Components that enable the user to exit the Inquiry flow
 */
type UiTerminalComponentConfig = ButtonCancelComponentConfig | ButtonCompleteComponentConfig;

export function isUiTerminalComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiTerminalComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.ButtonCancel:
    case UiComponentConfigType.ButtonComplete:
      return true;
    default:
      return false;
  }
}

export type UiButtonComponentConfig =
  | ButtonActionComponentConfig
  | ButtonCancelComponentConfig
  | ButtonCompleteComponentConfig
  | ButtonDeviceHandoffComponentConfig
  | ButtonLinkComponentConfig
  | ButtonSubmitComponentConfig
  | ButtonVerifyWithPersonaComponentConfig
  | CombinedStepButtonComponentConfig;

export function isUiButtonComponentConfig(
  component: UiComponentConfig | undefined,
): component is UiButtonComponentConfig {
  switch (component?.type) {
    case UiComponentConfigType.ButtonAction:
    case UiComponentConfigType.ButtonSubmit:
    case UiComponentConfigType.ButtonCancel:
    case UiComponentConfigType.ButtonComplete:
    case UiComponentConfigType.ButtonVerifyWithPersona:
    case UiComponentConfigType.CombinedStepButton:
      return true;
    default:
      return false;
  }
}

export function isStepperComponentConfig(
  component: UiComponentConfig | undefined,
): component is StepperComponentConfig {
  if (component?.type === UiComponentConfigType.Stepper) {
    return true;
  }
  return false;
}

export function isInputFileUploadComponentConfig(
  component: UiComponentConfig | undefined,
): component is InputFileUploadComponentConfig {
  if (component?.type === UiComponentConfigType.InputFileUpload) {
    return true;
  }
  return false;
}

export function isInputInternationalDbComponentConfig(
  component: UiComponentConfig | undefined,
): component is InputInternationalDbComponentConfig {
  if (component?.type === UiComponentConfigType.InputInternationalDb) {
    return true;
  }
  return false;
}

export function isInputCountrySelectComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is InputCountrySelectComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.InputCountrySelect:
      return true;
    default:
      return false;
  }
}

export interface BaseComponentConfig {
  type: UiComponentConfigType;
  name: string;
  config: Record<string, unknown>;
  'style-variants'?: {
    namespace: string;
    [k: string]: any;
  }[];
}

/**
 * Based on Hideable module:
 * app/models/inquiry/ui_component/config/hidable.rb
 *
 * Consists of both client and server jsonlogic
 * for visibility of component
 */
export type HideableLogicConfigAttributes = {
  hidden?: RulesLogic | null;
  'hidden-server-evaluated'?: RulesLogic | null;
};

/**
 * Consists of both client and server jsonlogic
 * for disabled state of component
 */
export type DisableableLogicConfigAttributes = {
  disabled?: RulesLogic | null;
  'disabled-server-evaluated'?: RulesLogic | null;
};

/**
 * Hack: This is a type and not an interface to allow nesting.
 */
export type TransitionConfigAttributes = {
  'to-step'?: string;
  'workflow-version-id'?: string;
  'workflow-on-error-step-name'?: string;
};

export type ButtonComponentConfigAttributes = HideableLogicConfigAttributes &
  DisableableLogicConfigAttributes & {
    'button-type'?: ButtonType;
    'text-translations'?: Translation[];
    'text-fallback-i18n-key'?: string;
  };

export type ButtonComponentConfigStyles = {
  namespace: string;
  // Layout
  height: StyleValueSizeConfig;
  width: StyleValueSizeConfig;
  justify: StyleValueJustify;
  margin: StyleValueBoxDimensionsConfig;
  padding: StyleValueBoxDimensionsConfig;
  translate: StyleValueXYDimensionsConfig;
  'translate--base--focused': StyleValueXYDimensionsConfig;
  'translate--base--active': StyleValueXYDimensionsConfig;
  'translate--base--disabled': StyleValueXYDimensionsConfig;
  // Text
  'font-family': StyleValueFontFamily;
  'font-size': StyleValueSizeConfig;
  'font-weight': StyleValueFontWeight;
  'letter-spacing': StyleValueSizeConfig;
  'line-height': StyleValueSizeConfig;
  'text-color': StyleValueColorConfig;
  'text-color--base--focused': StyleValueColorConfig;
  'text-color--base--active': StyleValueColorConfig;
  'text-color--base--disabled': StyleValueColorConfig;
  // Decoration
  'background-color': StyleValueColorConfig;
  'background-color--base--focused': StyleValueColorConfig;
  'background-color--base--active': StyleValueColorConfig;
  'background-color--base--disabled': StyleValueColorConfig;
  'border-color': StyleValueColorConfig;
  'border-color--base--focused': StyleValueColorConfig;
  'border-color--base--active': StyleValueColorConfig;
  'border-color--base--disabled': StyleValueColorConfig;
  'border-radius': StyleValueSizeConfig;
  'border-width': StyleValueBoxDimensionsConfig;
  'shadow-color': StyleValueColorConfig;
  'shadow-color--base--focused': StyleValueColorConfig;
  'shadow-color--base--active': StyleValueColorConfig;
  'shadow-color--base--disabled': StyleValueColorConfig;
  'shadow-offset': StyleValueXYDimensionsConfig;
  'shadow-offset--base--focused': StyleValueXYDimensionsConfig;
  'shadow-offset--base--active': StyleValueXYDimensionsConfig;
  'shadow-offset--base--disabled': StyleValueXYDimensionsConfig;
  'shadow-radius': StyleValueSizeConfig;
  'shadow-radius--base--focused': StyleValueSizeConfig;
  'shadow-radius--base--active': StyleValueSizeConfig;
  'shadow-radius--base--disabled': StyleValueSizeConfig;
};

export interface ButtonActionComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.ButtonAction;
  config: TransitionConfigAttributes &
    ButtonComponentConfigAttributes & {
      'workflow-messages'?: MessageConfig[];
    };
  'style-variants'?: ButtonComponentConfigStyles[];
}

export interface ButtonCancelComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.ButtonCancel;
  config: ButtonComponentConfigAttributes;
  'style-variants'?: ButtonComponentConfigStyles[];
}

export interface ButtonCompleteComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.ButtonComplete;
  config: ButtonComponentConfigAttributes;
  'style-variants'?: ButtonComponentConfigStyles[];
}

export interface ButtonDeviceHandoffComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.ButtonDeviceHandoff;
  config: ButtonComponentConfigAttributes & {
    'device-handoff-options': DeviceHandoffOption[];
    'device-handoff-list-style'?: 'outline' | 'outline_light' | 'button';
    'field-key-device-handoff-email': string | null;
    'field-key-device-handoff-phone-number': string | null;
    localizations: DeviceHandoffLocalizations;
    assets: {
      'icon-email': ImageHostedComponentConfig | null;
      'icon-sms': ImageHostedComponentConfig | null;
    };
  };
  'style-variants'?: ButtonComponentConfigStyles[];
}

export interface ButtonLinkComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.ButtonLink;
  config: HideableLogicConfigAttributes & {
    'button-type'?: ButtonType;
    'text-translations'?: Translation[];
    'text-fallback-i18n-key'?: string;
    link: string;
  };
  'style-variants'?: ButtonComponentConfigStyles[];
}

export interface ButtonSubmitComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.ButtonSubmit;
  config: TransitionConfigAttributes &
    ButtonComponentConfigAttributes & {
      'workflow-messages'?: MessageConfig[];
    };
  'style-variants'?: ButtonComponentConfigStyles[];
}

export interface ButtonVerifyWithPersonaComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.ButtonVerifyWithPersona;
  config: TransitionConfigAttributes &
    ButtonComponentConfigAttributes & {
      'logo-asset-id'?: string;
    };
  'style-variants'?: ButtonComponentConfigStyles[];
}

export interface CombinedStepButtonComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.CombinedStepButton;
  config: ButtonComponentConfigAttributes;
  'style-variants'?: ButtonComponentConfigStyles[];
}

export interface CombinedStepImagePreviewComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.CombinedStepImagePreview;
  config: never;
  'style-variants'?: {
    namespace: string;
    height: StyleValueSizeConfig;
    width: StyleValueSizeConfig;
    margin: StyleValueBoxDimensionsConfig;
    justify: StyleValueJustify;
  }[];
}

export interface FooterComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.Footer;
  config: {
    direction: ConfigValueDirection;
    children: UiComponentConfig[];
  };
  'style-variants'?: {
    namespace: string;
    'background-color': StyleValueColorConfig;
    padding: StyleValueBoxDimensionsConfig;
    justify: StyleValueJustify;
  }[];
}

export interface GovernmentIdNfcScanComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.GovernmentIdNfcScan;
  config: {
    'launch-button-text-translations'?: Translation[];
    'document-number-label-translations'?: Translation[];
    'date-of-birth-label-translations'?: Translation[];
    'required-text-translations'?: Translation[];
    'expiration-date-label-translations'?: Translation[];
    'scan-document-prompt-translations'?: Translation[];
    'scan-document-success-translations'?: Translation[];
    'authenticating-translations'?: Translation[];
    'reading-translations'?: Translation[];
    'reading-short-time-translations'?: Translation[];
    'reading-long-time-translations'?: Translation[];
    'scan-document-error-translations'?: Translation[];
    'enable-nfc-prompt-translations'?: Translation[];
    'authentication-error-prompt-translations'?: Translation[];
    'hide-prefilled-inputs'?: boolean;
    'required-if-supported'?: boolean;
    field?: string;
    'field-key-document-number': string;
    'field-key-date-of-birth': string;
    'field-key-expiration-date': string;
    'field-key-country-code': CountryCode;
  };
  'style-variants'?: {
    namespace: string;
    'button-primary-style': ButtonComponentStyles;
    'input-text-style': InputTextComponentConfigStyles;
    'input-date-style': InputDateComponentConfigStyles;
  }[];
}

type StackComponentConfigStyles = {
  namespace: string;
  margin: StyleValueBoxDimensionsConfig;
  padding: StyleValueBoxDimensionsConfig;
  'frame-positioning': StyleValueFramePositioning;
  gap: StyleValueSizeConfig;
  alignment: StyleValueAlignment;
  justify: StyleValueJustify;
  axis: StyleValueAxis;
  'background-color': StyleValueColorConfig;
  'border-color': StyleValueColorConfig;
  'border-width': StyleValueBoxDimensionsConfig;
  'border-radius': StyleValueSizeConfig;
  'child-sizes': number[];
};

export interface HorizontalStackComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.HorizontalStack;
  config: HideableLogicConfigAttributes & {
    children: UiComponentConfig[];
  };
  'style-variants'?: StackComponentConfigStyles[];
}

export interface ClickableStackComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.ClickableStack;
  config: TransitionConfigAttributes &
    HideableLogicConfigAttributes &
    DisableableLogicConfigAttributes & {
      children: UiComponentConfig[];
    };
  'style-variants'?: (StackComponentConfigStyles & {
    'background-color': StyleValueColorConfig;
    'background-color--base--focused': StyleValueColorConfig;
    'background-color--base--active': StyleValueColorConfig;
    'background-color--base--disabled': StyleValueColorConfig;
    'border-color': StyleValueColorConfig;
    'border-color--base--focused': StyleValueColorConfig;
    'border-color--base--active': StyleValueColorConfig;
    'border-color--base--disabled': StyleValueColorConfig;
  })[];
}

export type StepperStepLogicConfigAttributes = {
  'completed-server-evaluted': RulesLogic | null;
  'errored-server-evaluted': RulesLogic | null;
  'disabled-server-evaluated': RulesLogic | null;
};

export interface StepperStepConfig
  extends TransitionConfigAttributes,
    StepperStepLogicConfigAttributes {
  // Stepper step config
  'matching-steps': string[];
  'icon-current-asset-id-override': string | undefined;
  'icon-incomplete-asset-id-override': string | undefined;
  'icon-disabled-asset-id-override': string | undefined;
  'icon-completed-asset-id-override': string | undefined;
  'icon-errored-asset-id-override': string | undefined;

  // Translations Config
  'text-translations'?: Translation[];
  'text-fallback-i18n-key'?: string;
  'text-default-value'?: string;
  'text-fallback-i18n-dynamic-variables': I18nDynamicVariables;
}

export interface StepperComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.Stepper;
  config: DisableableLogicConfigAttributes & {
    'icon-current-asset-id': string | undefined;
    'icon-incomplete-asset-id': string | undefined;
    'icon-disabled-asset-id': string | undefined;
    'icon-completed-asset-id': string | undefined;
    'icon-errored-asset-id': string | undefined;
    'image-connector-asset-id': string | undefined;
    'disable-subsequent-steps-unless-completed': boolean;
    'disable-subsequent-steps-on-error': boolean;
    steps: StepperStepConfig[];
  };
  'style-variants'?: {
    namespace: string;
    'horizontal-stack-style': StackComponentStyles;
    'clickable-stack-style--stack-current': ClickableStackComponentConfig['style-variants'];
    'clickable-stack-style--stack-incomplete': ClickableStackComponentConfig['style-variants'];
    'clickable-stack-style--stack-disabled': ClickableStackComponentConfig['style-variants'];
    'clickable-stack-style--stack-completed': ClickableStackComponentConfig['style-variants'];
    'clickable-stack-style--stack-errored': ClickableStackComponentConfig['style-variants'];
    'image-hosted-style--icon-current': ImageHostedComponentConfig['style-variants'];
    'image-hosted-style--icon-incomplete': ImageHostedComponentConfig['style-variants'];
    'image-hosted-style--icon-disabled': ImageHostedComponentConfig['style-variants'];
    'image-hosted-style--icon-completed': ImageHostedComponentConfig['style-variants'];
    'image-hosted-style--icon-errored': ImageHostedComponentConfig['style-variants'];
    'image-hosted-style--image-connector': ImageHostedComponentConfig['style-variants'];
    'text-style--label-current': TextComponentConfig['style-variants'];
    'text-style--label-incomplete': TextComponentConfig['style-variants'];
    'text-style--label-disabled': TextComponentConfig['style-variants'];
    'text-style--label-completed': TextComponentConfig['style-variants'];
    'text-style--label-errored': TextComponentConfig['style-variants'];
  }[];
}

export enum AssetSource {
  ThemeVariable = 'theme_variable',
  ThemeSet = 'theme_set',
  InquiryTemplateVersion = 'inquiry_template_version',
}

export interface GovernmentIdTypeSelectComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.GovernmentIdTypeSelect;
  config: TransitionConfigAttributes &
    FieldComponentConfigAttributes & {
      'government-id-step-name': string;
      'field-key-selected-country-code-override': string | null;
      field: string;
      'icon-chevron': ImageHostedComponentConfig | null;
    };
  'style-variants'?: {
    namespace: string;
    'horizontal-stack-style': StackComponentStyles;
    'clickable-stack-style': ClickableStackComponentConfig['style-variants'];
    'image-local-style--icon-id': ImageLocalComponentConfig['style-variants'];
    'image-local-style--icon-chevron': ImageHostedComponentConfig['style-variants'];
    'text-style': TextComponentConfig['style-variants'];
  }[];
}

export interface ImageHostedComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.ImageHosted;
  config: HideableLogicConfigAttributes & {
    'asset-id'?: string;
    height?: string;
    width?: string;
    source?: AssetSource;
    alt?: string;
  };
  'style-variants'?: {
    namespace: string;
    justify: StyleValueJustify;
    margin: StyleValueBoxDimensionsConfig;
    height: StyleValueSizeConfig;
    width: StyleValueSizeConfig;
    'fill-color--fill': StyleValueColorConfig;
    'fill-color--background': StyleValueColorConfig;
    'fill-color--highlight': StyleValueColorConfig;
    'fill-color--original-fill': StyleValueColorConfig;
    'fill-color--original-background': StyleValueColorConfig;
    'fill-color--original-highlight': StyleValueColorConfig;
    'stroke-color--stroke': StyleValueColorConfig;
    'stroke-color--original-stroke': StyleValueColorConfig;
  }[];
}

interface ImageLocalComponentConfigIconStyles {
  'fill-color': StyleValueColorConfig;
  'fill-color--highlight': StyleValueColorConfig;
  'fill-color--background': StyleValueColorConfig;
  'stroke-color': StyleValueColorConfig;
}

export type ImageLocalIconStyleKeys = keyof ImageLocalComponentConfigIconStyles;

export interface ImageLocalComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.ImageLocal;
  config: HideableLogicConfigAttributes & {
    'image-key': ImageKey;
  };
  'style-variants'?: ({
    namespace: string;
    margin: StyleValueBoxDimensionsConfig;
    justify: StyleValueJustify;
    height: StyleValueSizeConfig;
    width: StyleValueSizeConfig;
  } & ImageLocalComponentConfigIconStyles)[];
}

/**
 * JSONLogic rule with associated error message.
 */
export type UiComponentConfigRule = {
  logic: RulesLogic;
  'message-translations'?: Translation[];
  'message-fallback-i18n-key'?: string;
  'message-default-value'?: string;
  'message-fallback-i18n-dynamic-variables'?: I18nDynamicVariables;
};

type LogicConfigAttributes = HideableLogicConfigAttributes &
  DisableableLogicConfigAttributes & {
    optional?: UiComponentConfigRule;
    validations?: UiComponentConfigRule[];
  };

/**
 * Common config shared across all form UI components.
 * Does not include label, or any attribute whose type varies based on
 * component type or input layout.
 * Unlike the client data version, does not include prefill, as that is not
 * configurable.
 * Hack: This is a type and not an interface to allow nesting.
 */
type FieldComponentConfigAttributes = LogicConfigAttributes & {
  field: string;
  'placeholder-default-value': string;
};

export type InputAddressComponentFieldConfig = {
  'field-key-address-street-1': string;
  'field-key-address-street-2': string;
  'field-key-address-city': string;
  'field-key-address-subdivision': string;
  'field-key-address-postal-code': string;
  'field-key-address-business-name': string | null;
  'field-key-address-country-code': CountryCode;
  'field-key-selected-country-code': CountryCode;
};

export type InputInternationalDbComponentFieldConfig = {
  'field-key-idb-country': string;
  'field-key-idb-type': string;
  'field-key-idb-value': string;
};

export const isComponentFieldConfigKey = (
  key: string,
): key is keyof InputMultiFieldComponentFieldConfig => key.startsWith('field-key-');

export type InputMultiFieldComponentFieldConfig =
  | InputAddressComponentFieldConfig
  | InputInternationalDbComponentFieldConfig;

/**
 * Typing for FormFieldOptionComponentConfig options
 */
export type Option = {
  key?: string;
  'text-default-value'?: string;
  'text-fallback-i18n-key'?: string;
  'text-translations'?: Translation[];
  value: string;
};

export interface InputAddressComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputAddress;
  config: LogicConfigAttributes &
    InputAddressComponentFieldConfig & {
      // validators
      'validations-address-street-1': UiComponentConfigRule[];
      'validations-address-street-2': UiComponentConfigRule[];
      'validations-address-city': UiComponentConfigRule[];
      'validations-address-subdivision': UiComponentConfigRule[];
      'validations-address-postal-code': UiComponentConfigRule[];
      'validations-address-business-name': UiComponentConfigRule[];
      // validation message translations
      'validate-city-us-required'?: Translation[];
      'validate-postal-code-gb-format'?: Translation[];
      'validate-postal-code-gb-required'?: Translation[];
      'validate-postal-code-us-format'?: Translation[];
      'validate-postal-code-us-required'?: Translation[];
      'validate-state-us-required'?: Translation[];
      'validate-street-1-required'?: Translation[];
      'validate-street-1-too-long'?: Translation[];
      // translations
      'edit-address-manually-prompt-translations'?: Translation[];
      'label-translations'?: Translation[];
      'placeholder-autocomplete-translations'?: Translation[];
      'placeholder-autocomplete-postal-code-translations'?: Translation[];
      'placeholder-address-street-1-translations'?: Translation[];
      'placeholder-address-street-2-translations'?: Translation[];
      'placeholder-address-city-translations'?: Translation[];
      'placeholder-address-subdivision-translations'?: Translation[];
      'placeholder-address-subdivision-us-translations'?: Translation[];
      'placeholder-address-postal-code-translations'?: Translation[];
      'placeholder-address-postal-code-us-translations'?: Translation[];
      'placeholder-address-business-name-translations'?: Translation[];
      // other
      'address-autocomplete-method': AddressAutocompleteMethod;
      'format-address-subdivision': FormatAddressSubdivision; // defaults to long_name
      // While Google edfines this as an array, only a single item is allowed.
      'place-type'?: PlaceType[];
    };
  'style-variants'?: (InputTextComponentConfigStyles & {
    'text-style--expand-button': TextComponentConfig['style-variants'];
  })[];
}

export interface InputInternationalDbComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputInternationalDb;
  config: LogicConfigAttributes &
    InputInternationalDbComponentFieldConfig & {
      'entity-type': InternationalDbEntityType;
      'allowed-id-types': AllowedIdType[];
      'hide-country-if-prefilled'?: boolean;
      'hide-type-if-prefilled'?: boolean;
      'hide-country-if-single-choice'?: boolean;
      'hide-type-if-single-choice'?: boolean;
      'label-translations'?: Translation[];
      'label-idb-country-translations'?: Translation[];
      'label-idb-type-translations'?: Translation[];
      'label-idb-value-translations'?: Translation[];
      'placeholder-idb-country-translations'?: Translation[];
      'placeholder-idb-type-translations'?: Translation[];
      'placeholder-idb-value-translations'?: Translation[];
    };
  'style-variants'?: {
    namespace: string;
    'input-select-style': InputSelectComponentConfig['style-variants'];
    'input-text-style': InputTextComponentConfig['style-variants'];
    'text-style': TextComponentConfig['style-variants'];
  }[];
}

type InternationalDbEntityType = 'individual' | 'business';

export interface AllowedIdType {
  'country-code': string;
  'id-type': string;
}

export interface InputCheckboxComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputCheckbox;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
  };
  'style-variants'?: {
    namespace: string;
    alignment: StyleValueAlignment;
    'font-family': StyleValueFontFamily;
    'font-family--error': StyleValueFontFamily;
    'font-size': StyleValueSizeConfig;
    'font-size--error': StyleValueSizeConfig;
    'font-weight': StyleValueFontWeight;
    'font-weight--error': StyleValueFontWeight;
    'letter-spacing': StyleValueSizeConfig;
    'letter-spacing--error': StyleValueSizeConfig;
    'line-height': StyleValueSizeConfig;
    'line-height--error': StyleValueSizeConfig;
    'text-color': StyleValueColorConfig;
    'text-color--error': StyleValueColorConfig;
    'text-color-highlight': StyleValueColorConfig;
    'fill-color--checkbox-checked': StyleValueColorConfig;
    'fill-color--checkbox-checked--focused': StyleValueColorConfig;
    'fill-color--checkbox-checked--active': StyleValueColorConfig;
    'fill-color--checkbox-checked--disabled': StyleValueColorConfig;
    'fill-color--checkbox-unchecked': StyleValueColorConfig;
    'fill-color--checkbox-unchecked--focused': StyleValueColorConfig;
    'fill-color--checkbox-unchecked--active': StyleValueColorConfig;
    'fill-color--checkbox-unchecked--disabled': StyleValueColorConfig;
    'stroke-color--checkbox-checked': StyleValueColorConfig;
    'stroke-color--checkbox-checked--focused': StyleValueColorConfig;
    'stroke-color--checkbox-checked--active': StyleValueColorConfig;
    'stroke-color--checkbox-checked--disabled': StyleValueColorConfig;
    'stroke-color--checkbox-unchecked': StyleValueColorConfig;
    'stroke-color--checkbox-unchecked--focused': StyleValueColorConfig;
    'stroke-color--checkbox-unchecked--active': StyleValueColorConfig;
    'stroke-color--checkbox-unchecked--disabled': StyleValueColorConfig;
    'stroke-color--checkmark': StyleValueColorConfig;
  }[];
}

export interface InputCheckboxDatetimeComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputCheckboxDatetime;
  config: InputCheckboxComponentConfig['config'];
  'style-variants'?: InputCheckboxComponentConfig['style-variants'];
}

export interface InputCheckboxGroupComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputCheckboxGroup;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    options: Array<Option>;
  };
  'style-variants'?: {
    namespace: string;
    alignment: StyleValueAlignment;
    'font-family': StyleValueFontFamily;
    'font-size': StyleValueSizeConfig;
    'font-weight': StyleValueFontWeight;
    'letter-spacing': StyleValueSizeConfig;
    'line-height': StyleValueSizeConfig;
    'text-color': StyleValueColorConfig;
    'text-color-highlight': StyleValueColorConfig;
  }[];
}

export interface InputConfirmationCodeComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputConfirmationCode;
  config: LogicConfigAttributes & {
    'error-messages': MessageConfig[];
  };
}

export interface InputCountrySelectComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputCountrySelect;
  config: FieldComponentConfigAttributes & {
    'country-codes': CountryCode[];
    'placeholder-translations'?: Translation[];
    'label-translations'?: Translation[];
  };
  'style-variants'?: InputSelectComponentConfig['style-variants'];
}

type InputDateComponentConfigStyles = {
  namespace: string;
  'font-family': StyleValueFontFamily;
  'font-family--value': StyleValueFontFamily;
  'font-family--placeholder': StyleValueFontFamily;
  'font-family--label': StyleValueFontFamily;
  'font-family--error': StyleValueFontFamily;
  'font-size--value': StyleValueSizeConfig;
  'font-size--placeholder': StyleValueSizeConfig;
  'font-size--label': StyleValueSizeConfig;
  'font-size--error': StyleValueSizeConfig;
  'font-weight--value': StyleValueFontWeight;
  'font-weight--placeholder': StyleValueFontWeight;
  'font-weight--label': StyleValueFontWeight;
  'font-weight--error': StyleValueFontWeight;
  'letter-spacing': StyleValueSizeConfig;
  'letter-spacing--value': StyleValueSizeConfig;
  'letter-spacing--placeholder': StyleValueSizeConfig;
  'letter-spacing--label': StyleValueSizeConfig;
  'letter-spacing--error': StyleValueSizeConfig;
  'line-height--value': StyleValueSizeConfig;
  'line-height--placeholder': StyleValueSizeConfig;
  'line-height--label': StyleValueSizeConfig;
  'line-height--error': StyleValueSizeConfig;
  margin: StyleValueBoxDimensionsConfig;
  'text-color--value': StyleValueColorConfig;
  'text-color--placeholder': StyleValueColorConfig;
  'text-color--label': StyleValueColorConfig;
  'text-color--error': StyleValueColorConfig;
  'background-color': StyleValueColorConfig;
  'background-color--focused': StyleValueColorConfig;
  'background-color--disabled': StyleValueColorConfig;
  'background-color--errored': StyleValueColorConfig;
  'border-color': StyleValueColorConfig;
  'border-color--focused': StyleValueColorConfig;
  'border-color--disabled': StyleValueColorConfig;
  'border-color--errored': StyleValueColorConfig;
  'border-radius': StyleValueSizeConfig;
  'border-width': StyleValueBoxDimensionsConfig;
  'shadow-color': StyleValueColorConfig;
  'shadow-offset': StyleValueXYDimensionsConfig;
  'shadow-radius': StyleValueSizeConfig;
};

/**
 * app/models/inquiry/ui_component/input_date.rb
 **/

export enum TimePeriodType {
  Days = 'days',
  Months = 'months',
  Years = 'years',
}
export interface RelativeDate {
  quantity: number;
  'time-period': TimePeriodType;
}

export interface InputDateComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputDate;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'placeholder-month-translations'?: Translation[];
    'placeholder-day-translations'?: Translation[];
    'placeholder-year-translations'?: Translation[];
    'text-months-translations'?: Translation[];
    'date-range-start': string | null;
    'date-range-end': string | null;
    'relative-start-date': RelativeDate | null;
    'relative-end-date': RelativeDate | null;
    timezone: string | null;
  };
  'style-variants'?: InputDateComponentConfigStyles[];
}

export interface InputESignatureComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputESignature;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'placeholder-translations'?: Translation[];
    // Allow specifying different dialog text for full signature vs. initials
    'dialog-title-full-translations'?: Translation[];
    'dialog-title-initials-translations'?: Translation[];
    'dialog-text-full-translations'?: Translation[];
    'dialog-text-initials-translations'?: Translation[];
    'save-button-text-translations'?: Translation[];
    'clear-button-text-translations'?: Translation[];
    'signature-type': 'full' | 'initials';
  };
  'style-variants'?: Array<
    InputTextComponentConfigStyles & {
      'button-primary-style--save-signature-button': ButtonComponentStyles;
      'button-secondary-style--clear-signature-button': ButtonComponentStyles;
      'title-style--dialog-title': TitleComponentStyles;
      'text-style--dialog-text': TextComponentStyles;
      'fill-color': StyleValueColorConfig;
      'background-color--signature-preview': StyleValueColorConfig;
    }
  >;
}

export interface InputMaskedTextComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputMaskedText;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'placeholder-translations'?: Translation[];
    secure?: boolean;
    mask?: string;
  };
}

export interface InputMultiSelectComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputMultiSelect;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'placeholder-translations'?: Translation[];
    options: Array<Option>;
  };
}

export enum PhoneNumberFormat {
  Auto = 'auto',
  National = 'national',
  International = 'international',
}

export interface InputPhoneNumberComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputPhoneNumber;
  config: FieldComponentConfigAttributes & {
    'field-key-country-code'?: string;
    'error-messages': MessageConfig[];
    'label-translations'?: Translation[];
    'placeholder-translations'?: Translation[];
    'country-code-field'?: string;
    'disable-country-code-selection'?: boolean;
    'phone-number-format'?: PhoneNumberFormat;
  };
}

export interface InputRadioGroupComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputRadioGroup;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    options: Array<Option>;
  };
}

export interface InputSelectComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputSelect;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'placeholder-translations'?: Translation[];
    'field-key-options'?: string;
    options: Array<Option>;
  };
  'style-variants'?: {
    namespace: string;
    'font-family': StyleValueFontFamily;
    'font-family--error': StyleValueFontFamily;
    'font-family--label': StyleValueFontFamily;
    'font-size': StyleValueSizeConfig;
    'font-size--error': StyleValueSizeConfig;
    'font-size--label': StyleValueSizeConfig;
    'font-size--value': StyleValueSizeConfig;
    'font-weight': StyleValueFontWeight;
    'font-weight--error': StyleValueFontWeight;
    'font-weight--label': StyleValueFontWeight;
    'letter-spacing--error': StyleValueSizeConfig;
    'letter-spacing': StyleValueSizeConfig;
    'line-height': StyleValueSizeConfig;
    'line-height--error': StyleValueSizeConfig;
    margin: StyleValueBoxDimensionsConfig;
    'margin--option': StyleValueBoxDimensionsConfig;
    justify: StyleValueJustify;
    'text-color': StyleValueColorConfig;
    'text-color--label': StyleValueColorConfig;
    'text-color--value': StyleValueColorConfig;
    'text-color--error': StyleValueColorConfig;
    'text-color--label--focused': StyleValueColorConfig;
    'text-color--value--focused': StyleValueColorConfig;
    'background-color': StyleValueColorConfig;
    'background-color--focused': StyleValueColorConfig;
    'background-color--option': StyleValueColorConfig;
    'background-color--option--active': StyleValueColorConfig;
    'background-color--option--focused': StyleValueColorConfig;
    'border-color': StyleValueColorConfig;
    'border-color--active': StyleValueColorConfig;
    'border-color--focused': StyleValueColorConfig;
    'border-color--errored': StyleValueColorConfig;
    'border-color--disabled': StyleValueColorConfig;
    'border-radius': StyleValueSizeConfig;
    'border-width': StyleValueBoxDimensionsConfig;
    'stroke-color--chevron': StyleValueColorConfig;
    'stroke-color--header-cancel-button': StyleValueColorConfig;
    'shadow-color': StyleValueColorConfig;
    'shadow-offset': StyleValueXYDimensionsConfig;
    'shadow-radius': StyleValueSizeConfig;
  }[];
}

export interface InputSubdivisionSelectComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputSubdivisionSelect;
  config: FieldComponentConfigAttributes &
    Pick<InputAddressComponentFieldConfig, 'field-key-selected-country-code'> & {
      'country-codes': CountryCode[];
      'label-translations'?: Translation[];
      'placeholder-translations'?: Translation[];
      'subdivision-exclusions': Record<CountryCode, string[]>;
      'subdivision-options': string[];
    };
  'style-variants'?: InputSelectComponentConfig['style-variants'];
}

type InputTextComponentConfigStyles = {
  namespace: string;
  'font-family': StyleValueFontFamily;
  'font-family--value': StyleValueFontFamily;
  'font-family--placeholder': StyleValueFontFamily;
  'font-family--label': StyleValueFontFamily;
  'font-family--errored': StyleValueFontFamily;
  'font-size--value': StyleValueSizeConfig;
  'font-size--placeholder': StyleValueSizeConfig;
  'font-size--label': StyleValueSizeConfig;
  'font-size--errored': StyleValueSizeConfig;
  'font-weight--value': StyleValueFontWeight;
  'font-weight--placeholder': StyleValueFontWeight;
  'font-weight--label': StyleValueFontWeight;
  'font-weight--errored': StyleValueFontWeight;
  'letter-spacing': StyleValueSizeConfig;
  'letter-spacing--value': StyleValueSizeConfig;
  'letter-spacing--placeholder': StyleValueSizeConfig;
  'letter-spacing--label': StyleValueSizeConfig;
  'letter-spacing--errored': StyleValueSizeConfig;
  'line-height--value': StyleValueSizeConfig;
  'line-height--placeholder': StyleValueSizeConfig;
  'line-height--label': StyleValueSizeConfig;
  'line-height--errored': StyleValueSizeConfig;
  'text-color--value': StyleValueColorConfig;
  'text-color--placeholder': StyleValueColorConfig;
  'text-color--label': StyleValueColorConfig;
  'text-color--errored': StyleValueColorConfig;
  'background-color': StyleValueColorConfig;
  'background-color--focused': StyleValueColorConfig;
  'background-color--disabled': StyleValueColorConfig;
  'background-color--errored': StyleValueColorConfig;
  'border-color': StyleValueColorConfig;
  'border-color--focused': StyleValueColorConfig;
  'border-color--disabled': StyleValueColorConfig;
  'border-color--errored': StyleValueColorConfig;
  'border-radius': StyleValueSizeConfig;
  'border-width': StyleValueBoxDimensionsConfig;
  'shadow-color': StyleValueColorConfig;
  'shadow-offset': StyleValueXYDimensionsConfig;
  'shadow-radius': StyleValueSizeConfig;
};

export interface InputTextComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputText;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'placeholder-translations'?: Translation[];
    'input-type'?: InputType;
    'autofill-hint'?: AutofillHint;
  };
  'style-variants'?: InputTextComponentConfigStyles[];
}

export interface InputUrlComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputUrl;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'placeholder-translations'?: Translation[];
    'allowed-uri-schemes'?: string[];
  };
  'style-variants'?: InputTextComponentConfigStyles[];
}
export interface InputCurrencyComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputCurrency;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'placeholder-translations'?: Translation[];
    'field-key-currency-code'?: string;
    'default-currency-code'?: string;
  };
  'style-variants'?: InputTextComponentConfigStyles[];
}

export interface InputNumberComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputNumber;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'placeholder-translations'?: Translation[];
    'input-type'?: InputType;
  };
  'style-variants'?: InputTextComponentConfigStyles[];
}

export interface InputTextAreaComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputTextArea;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'placeholder-translations'?: Translation[];
    'allow-resize': boolean;
    rows: number;
  };
}

export interface InputFileUploadComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.InputFileUpload;
  config: FieldComponentConfigAttributes & {
    'label-translations'?: Translation[];
    'file-size-limit': number;
    'file-upload-limit': number;
    'allowed-file-types': string[];
  };
  'style-variants'?: InputTextComponentConfigStyles[];
}

// language selector server component has null config, as the enabled-locales
// are pulled directly from the ITV (see language_selector.rb)
/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export interface LanguageSelectorComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.LanguageSelector;
  config: null;
}

export interface PasskeyRegistrationComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.PasskeyRegistration;
  config: never;
}

export interface PasskeyAuthenticationComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.PasskeyAuthentication;
  config: never;
}

export interface PersonaBrandingComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.PersonaBranding;
  config: never;
}

export interface PrivacyPolicyComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.PrivacyPolicy;
  config: HideableLogicConfigAttributes & {
    'text-translations'?: Translation[];
    'text-fallback-i18n-key'?: string;
  };
  'style-variants'?: {
    namespace: string;
    margin: StyleValueBoxDimensionsConfig;
    justify: StyleValueJustify;
    'font-family': StyleValueFontFamily;
    'font-size': StyleValueSizeConfig;
    'font-weight': StyleValueFontWeight;
    'letter-spacing': StyleValueSizeConfig;
    'line-height': StyleValueSizeConfig;
    'text-color': StyleValueColorConfig;
  }[];
}

export interface QrCodeComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.QrCode;
  config: HideableLogicConfigAttributes & {
    value: string;
  };
  'style-variants'?: {
    namespace: string;
    width: StyleValueSizeConfig;
    margin: StyleValueBoxDimensionsConfig;
    justify: StyleValueJustify;
    'fill-color': StyleValueColorConfig;
    'stroke-color': StyleValueColorConfig;
  }[];
}

export interface SpacerComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.Spacer;
  config: HideableLogicConfigAttributes & {
    height: string;
  };
  'style-variants'?: {
    namespace: string;
    height: StyleValueSizeConfig;
    width: StyleValueSizeConfig;
  }[];
}

interface Cell {
  'text-translations'?: Translation[];
}

export interface TableStaticComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.TableStatic;
  config: {
    header: Cell[];
    rows: Cell[][];
  };
}

export interface TextComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.Text;
  config: HideableLogicConfigAttributes & {
    'text-translations'?: Translation[];
    'text-fallback-i18n-key'?: string;
    'text-default-value'?: string;
  };
  'style-variants'?: {
    namespace: string;
    margin: StyleValueBoxDimensionsConfig;
    justify: StyleValueJustify;
    'font-family': StyleValueFontFamily;
    'font-size': StyleValueSizeConfig;
    'font-weight': StyleValueFontWeight;
    'letter-spacing': StyleValueSizeConfig;
    'line-height': StyleValueSizeConfig;
    'text-color': StyleValueColorConfig;
    'text-color-highlight': StyleValueColorConfig;
  }[];
}

export interface TitleComponentConfig extends BaseComponentConfig {
  type: UiComponentConfigType.Title;
  config: HideableLogicConfigAttributes & {
    'text-translations'?: Translation[];
    'text-fallback-i18n-key'?: string;
    'text-default-value'?: string;
  };
  'style-variants'?: {
    namespace: string;
    margin: StyleValueBoxDimensionsConfig;
    justify: StyleValueJustify;
    'font-family': StyleValueFontFamily;
    'font-size': StyleValueSizeConfig;
    'font-weight': StyleValueFontWeight;
    'letter-spacing': StyleValueSizeConfig;
    'line-height': StyleValueSizeConfig;
    'text-color': StyleValueColorConfig;
    'text-color-highlight': StyleValueColorConfig;
  }[];
}

/**
 * Styling interfaces
 */

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
type StylingVariantNamespace = Pick<BaseComponentConfig['style-variants'][number], 'namespace'>;

/**
 * SIZES
 */

export interface SizeComponent extends BaseComponent {
  styles?: BaseComponent['styles'] & {
    height?: Style<StyleKey.Height>;
    width?: Style<StyleKey.Width>;
  };
}

export interface SizeComponentConfig extends BaseComponentConfig {
  'style-variants'?: (StylingVariantNamespace &
    Partial<{
      height: StyleValueSizeConfig;
      width: StyleValueSizeConfig;
    }>)[];
}

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export type SizingKeys = keyof Omit<SizeComponentConfig['style-variants'][number], 'namespace'>;
export type SizingUnitTypes = Pick<StyleValueSizeConfig, 'unit'>[keyof Pick<
  StyleValueSizeConfig,
  'unit'
>];

export const canStyleWidth = (
  componentConfig: BaseComponentConfig,
  namespace?: string,
): boolean => {
  return (
    'width' in
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    componentConfig['style-variants'].find((style) => style.namespace === namespace || 'base')
  );
};

export const canStyleHeight = (
  componentConfig: BaseComponentConfig,
  namespace?: string,
): boolean => {
  return (
    'height' in
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    componentConfig['style-variants'].find((style) => style.namespace === namespace || 'base')
  );
};

export const canStyleSize = (
  componentConfig: BaseComponentConfig,
  namespace?: string,
): componentConfig is SizeComponentConfig => {
  return canStyleHeight(componentConfig, namespace) || canStyleWidth(componentConfig, namespace);
};

/**
 * SPACING
 */
export interface SpacingComponentConfig extends BaseComponentConfig {
  'style-variants'?: (StylingVariantNamespace &
    Partial<{
      margin?: StyleValueBoxDimensionsConfig;
      padding?: StyleValueBoxDimensionsConfig;
    }>)[];
}

export interface SpacingComponent extends BaseComponent {
  styles?: BaseComponent['styles'] &
    Partial<{
      margin: Style<StyleKey.Margin>;
      padding: Style<StyleKey.Padding>;
    }>;
}

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export type SpacingKeys = keyof Omit<SpacingComponentConfig['style-variants'][number], 'namespace'>;

const spacingKeys: SpacingKeys[] = ['margin', 'padding'];

export const canStyleSpacing = (
  componentConfig: BaseComponentConfig,
  namespace?: string,
): componentConfig is SpacingComponentConfig => {
  return spacingKeys.some(
    (key) =>
      key in
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      componentConfig['style-variants'].find((style) => style.namespace === namespace || 'base'),
  );
};

/**
 * TEXT
 */

export interface TextStylingComponent extends BaseComponent {
  styles?: Record<string, unknown> & {
    'font-family': Style<StyleKey.FontFamily>;
    'font-weight': Style<StyleKey.FontWeight>;
    'font-size': Style<StyleKey.FontSize>;
    'line-height': Style<StyleKey.LineHeight>;
    'letter-spacing': Style<StyleKey.LetterSpacing>;
    'text-color': Style<StyleKey.TextColor, 'base' | string>;
    'text-color-highlight'?: Style<StyleKey.TextColorHighlight>;
    justify?: Style<StyleKey.Justify>;
    alignment?: Style<StyleKey.Alignment>;
  };
}

export interface TextStylingComponentConfig extends BaseComponentConfig {
  'style-variants'?: (StylingVariantNamespace & {
    'font-family': StyleValueFontFamily;
    'font-weight': StyleValueFontWeight;
    'font-size': StyleValueSizeConfig;
    'line-height': StyleValueSizeConfig;
    'letter-spacing': StyleValueSizeConfig;
    'text-color': StyleValueColorConfig;
    'text-color-highlight'?: StyleValueColorConfig;
    justify?: StyleValueJustify;
    alignment?: StyleValueAlignment;
  })[];
}

export type TextKeys = keyof Omit<
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  TextStylingComponentConfig['style-variants'][number],
  'namespace'
>;

// These keys are not defined on all text-type components
type OptionalDefinedTextKeys = Extract<TextKeys, 'alignment' | 'justify' | 'text-color-highlight'>;
export type TextLayoutKeys = Extract<TextKeys, 'alignment' | 'justify'>;

const textKeys: TextKeys[] = [
  'font-family',
  'font-weight',
  'font-size',
  'line-height',
  'letter-spacing',
  'text-color',
  'justify',
  'alignment',
  'text-color-highlight',
];

export const canStyleText = (
  componentConfig: BaseComponentConfig,
  namespace?: string,
): componentConfig is TextStylingComponentConfig => {
  const optionallyDefinedTextKeys: OptionalDefinedTextKeys[] = [
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    'alignment',
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    'justify',
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    'text-color-highlight',
  ];
  const textBaseKeys = textKeys.filter(
    (key: TextKeys) => !optionallyDefinedTextKeys.includes(key as OptionalDefinedTextKeys),
  );
  const isKeyInNamespace = (key: TextKeys) =>
    key in
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    componentConfig['style-variants'].find((style) => style.namespace === namespace || 'base');

  return textBaseKeys.every(isKeyInNamespace);
};

/**
 * COLOR
 */
export interface ColorComponent extends BaseComponent {
  styles?: BaseComponent['styles'] &
    Partial<{
      'background-color': Style<StyleKey.BackgroundColor, 'base' | string>;
      'fill-color': Style<StyleKey.FillColor, 'base' | string>;
      'stroke-color': Style<StyleKey.StrokeColor, 'base' | string>;
    }>;
}

export interface ColorComponentConfig extends BaseComponentConfig {
  'style-variants'?: (StylingVariantNamespace &
    Partial<{
      'background-color'?: StyleValueColorConfig;
      'fill-color'?: StyleValueColorConfig;
      'stroke-color'?: StyleValueColorConfig;
    }>)[];
}

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export type ColorKeys = keyof Omit<ColorComponentConfig['style-variants'][number], 'namespace'>;

const colorKeys: ColorKeys[] = ['background-color', 'stroke-color', 'fill-color'];

export const canStyleColors = (
  componentConfig: BaseComponentConfig,
  namespace?: string,
): componentConfig is ColorComponentConfig => {
  return colorKeys.some(
    (key) =>
      key in
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      componentConfig['style-variants'].find((style) => style.namespace === namespace || 'base'),
  );
};

/**
 * BORDER
 */
export interface BorderStylesComponentConfig extends BaseComponentConfig {
  'style-variants'?: (StylingVariantNamespace & {
    'border-width': StyleValueBoxDimensions;
    'border-radius': StyleValueSizeConfig;
    'border-color': StyleValueColorConfig;
  })[];
}

export interface BorderStylesComponent extends BaseComponent {
  styles?: Record<string, unknown> & {
    'border-width': Style<StyleKey.BorderWidth>;
    'border-radius': Style<StyleKey.BorderRadius>;
    'border-color': Style<StyleKey.BorderColor>;
  };
}

export type BorderStyleKeys = keyof Omit<
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  BorderStylesComponentConfig['style-variants'][number],
  'namespace'
>;

const borderKeys: BorderStyleKeys[] = ['border-color', 'border-width', 'border-radius'];

export const canStyleBorders = (
  componentConfig: BaseComponentConfig,
  namespace?: string,
): componentConfig is BorderStylesComponentConfig => {
  return borderKeys.every(
    (key) =>
      key in
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      componentConfig['style-variants'].find((style) => style.namespace === namespace || 'base'),
  );
};

/**
 * LAYOUT
 */
export interface LayoutComponent extends BaseComponent {
  styles?: Record<string, unknown> &
    Partial<{
      alignment: Style<StyleKey.Alignment>;
      justify: Style<StyleKey.Justify>;
    }>;
}

export interface LayoutComponentConfig extends BaseComponentConfig {
  'style-variants'?: (StylingVariantNamespace &
    Partial<{
      alignment: StyleValueAlignment;
      justify: StyleValueJustify;
    }>)[];
}

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export type LayoutKeys = keyof Omit<LayoutComponentConfig['style-variants'][number], 'namespace'>;

const layoutKeys: LayoutKeys[] = ['alignment', 'justify'];

export const canStyleLayout = (
  componentConfig: BaseComponentConfig,
  namespace?: string,
): componentConfig is LayoutComponentConfig => {
  return layoutKeys.some(
    (key) =>
      key in
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      componentConfig['style-variants'].find((style) => style.namespace === namespace || 'base'),
  );
};

/**
 * LAYOUT (PARENT COMPONENTS)
 */
export interface ParentLayoutComponent extends BaseComponent {
  styles?: Record<string, unknown> & {
    alignment: Style<StyleKey.Alignment>;
    justify: Style<StyleKey.Justify>;
    gap: Style<StyleKey.Gap>;
    axis: Style<StyleKey.Axis>;
    'frame-positioning': Style<StyleKey.FramePositioning>;
    'child-sizes': Style<StyleKey.ChildSizes>;
  };
}

export interface ParentLayoutComponentConfig extends BaseComponentConfig {
  config: {
    children: UiComponentConfig[];
  };
  'style-variants'?: (StylingVariantNamespace &
    Partial<{
      alignment: StyleValueAlignment;
      justify: StyleValueJustify;
      gap: StyleValueSizeConfig;
      axis: StyleValueAxis;
      'frame-positioning': StyleValueFramePositioning;
      'child-sizes': number[];
    }>)[];
}

export type ParentLayoutKeys = keyof Omit<
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ParentLayoutComponentConfig['style-variants'][number],
  'namespace'
>;
const parentLayoutKeys: ParentLayoutKeys[] = ['alignment', 'justify', 'gap', 'axis', 'child-sizes'];

export const canStyleParentLayout = (
  componentConfig: BaseComponentConfig,
  namespace?: string,
): componentConfig is ParentLayoutComponentConfig => {
  return parentLayoutKeys.every(
    (key) =>
      key in
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      componentConfig['style-variants'].find((style) => style.namespace === namespace || 'base'),
  );
};

/**
 * Components that support parent styling
 */
export type UiStylableParentComponentConfig = Exclude<
  UiParentComponentConfig,
  FooterComponentConfig
>;

export function isStylableUiParentComponentConfig(
  componentConfig: UiComponentConfig | undefined,
): componentConfig is UiStylableParentComponentConfig {
  switch (componentConfig?.type) {
    case UiComponentConfigType.ClickableStack:
    case UiComponentConfigType.HorizontalStack:
      return true;
    default:
      return false;
  }
}

export type UiStylableParentComponent = Exclude<UiParentComponent, FooterComponent>;

export function isStylableUiParentComponent(
  component: UiComponent | undefined,
): component is UiStylableParentComponent {
  switch (component?.type) {
    case UiComponentType.ClickableStack:
    case UiComponentType.HorizontalStack:
      return true;
    default:
      return false;
  }
}

export type UiComponentConfigToClientDataMap = {
  [UiComponentConfigType.ButtonAction]: UiComponentType.ButtonAction;
  [UiComponentConfigType.ButtonCancel]: UiComponentType.ButtonCancel;
  [UiComponentConfigType.ButtonComplete]: UiComponentType.ButtonComplete;
  [UiComponentConfigType.ButtonDeviceHandoff]: UiComponentType.ButtonDeviceHandoff;
  [UiComponentConfigType.ButtonLink]: UiComponentType.ButtonLink;
  [UiComponentConfigType.ButtonSubmit]: UiComponentType.ButtonSubmit;
  [UiComponentConfigType.ButtonVerifyWithPersona]: UiComponentType.ButtonVerifyWithPersona;
  [UiComponentConfigType.CombinedStepButton]: UiComponentType.CombinedStepButton;
  [UiComponentConfigType.CombinedStepImagePreview]: UiComponentType.CombinedStepImagePreview;
  [UiComponentConfigType.ClickableStack]: UiComponentType.ClickableStack;
  [UiComponentConfigType.FailureReasonText]: UiComponentType.Text;
  [UiComponentConfigType.FailureReasonImage]: UiComponentType.ImageRemote;
  [UiComponentConfigType.Footer]: UiComponentType.Footer;
  [UiComponentConfigType.GovernmentIdNfcScan]: UiComponentType.GovernmentIdNfcScan;
  [UiComponentConfigType.GovernmentIdTypeSelect]: UiComponentType.GovernmentIdTypeSelect;
  [UiComponentConfigType.HorizontalStack]: UiComponentType.HorizontalStack;
  [UiComponentConfigType.ImageHosted]: UiComponentType.ImageRemote;
  [UiComponentConfigType.ImageLocal]: UiComponentType.ImageLocal;
  [UiComponentConfigType.InputAddress]: UiComponentType.InputAddress;
  [UiComponentConfigType.InputCheckbox]: UiComponentType.InputCheckbox;
  [UiComponentConfigType.InputCheckboxDatetime]: UiComponentType.InputCheckbox;
  [UiComponentConfigType.InputCheckboxGroup]: UiComponentType.InputCheckboxGroup;
  [UiComponentConfigType.InputConfirmationCode]: UiComponentType.InputConfirmationCode;
  [UiComponentConfigType.InputCountrySelect]: UiComponentType.InputSelect;
  [UiComponentConfigType.InputCurrency]: UiComponentType.InputCurrency;
  [UiComponentConfigType.InputDate]: UiComponentType.InputDate;
  [UiComponentConfigType.InputESignature]: UiComponentType.InputESignature;
  [UiComponentConfigType.InputFileUpload]: UiComponentType.InputFileUpload;
  [UiComponentConfigType.InputInternationalDb]: UiComponentType.InputInternationalDb;
  [UiComponentConfigType.InputMaskedText]: UiComponentType.InputMaskedText;
  [UiComponentConfigType.InputMultiSelect]: UiComponentType.InputMultiSelect;
  [UiComponentConfigType.InputNumber]: UiComponentType.InputNumber;
  [UiComponentConfigType.InputPhoneNumber]: UiComponentType.InputPhoneNumber;
  [UiComponentConfigType.InputRadioGroup]: UiComponentType.InputRadioGroup;
  [UiComponentConfigType.InputSelect]: UiComponentType.InputSelect;
  [UiComponentConfigType.InputSubdivisionSelect]: UiComponentType.InputSelect;
  [UiComponentConfigType.InputText]: UiComponentType.InputText;
  [UiComponentConfigType.InputTextArea]: UiComponentType.InputTextArea;
  [UiComponentConfigType.InputUrl]: UiComponentType.InputText;
  [UiComponentConfigType.LanguageSelector]: UiComponentType.LanguageSelector;
  [UiComponentConfigType.PasskeyAuthentication]: UiComponentType.PasskeyAuthentication;
  [UiComponentConfigType.PasskeyRegistration]: UiComponentType.PasskeyRegistration;
  [UiComponentConfigType.PersonaBranding]: UiComponentType.PersonaBranding;
  [UiComponentConfigType.PrivacyPolicy]: UiComponentType.PrivacyPolicy;
  [UiComponentConfigType.QrCode]: UiComponentType.QrCode;
  [UiComponentConfigType.Spacer]: UiComponentType.Spacer;
  [UiComponentConfigType.Stepper]: UiComponentType.Stepper;
  [UiComponentConfigType.TableStatic]: UiComponentType.TableStatic;
  [UiComponentConfigType.Text]: UiComponentType.Text;
  [UiComponentConfigType.Title]: UiComponentType.Title;
};
